import { useConfirm } from "../context/ConfirmContext";
import { useToast } from "../context/ToastContext";
import { SkeletonTable } from "../components/Skeleton";
import { AsyncButton } from "../components/AsyncButton";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { useBlockingDownload } from "../hooks/useBlockingDownload";
import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getCandidates, getCandidate, downloadFormattedCV, downloadFormattedDocx, downloadRawCV,
  updateStatus, regenerateCV, updateCandidateDetails, deleteApplication,
  deleteCandidate, bulkUpdateStatus, getInterviews, scheduleInterview,
  deleteInterview, getEmployees, generateInterviewQuestions, compareCandidates,
  generateOutreachEmail, sendOutreachEmail, getOutreachHistory, getJobs,
  getCandidateProfilePdfUrl, listScreeningTemplates, createScreeningSession,
  proposeInterviewSlots, getCandidateNotes, addCandidateNote, deleteCandidateNote, attachCV,
  getScreeningDefaults,
} from "../api";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useIsMobile } from "../hooks/useIsMobile";
import { TabPillBar } from "../components/TabPillBar";
import TimezoneSelect, { browserTimezone } from "../components/TimezoneSelect";
import { DateInput } from "../components/DateInput";
import { formatDate, formatDateTime } from "../utils/dateFormat";
import { useTenantTimezone } from "../hooks/useTenantTimezone";

const STATUS_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  new:          { bg: "#f1f5f9", text: "#475569", border: "#cbd5e1" },
  reviewed:     { bg: "#eff6ff", text: "#1d4ed8", border: "#bfdbfe" },
  shortlisted:  { bg: "#f0fdf4", text: "#166534", border: "#86efac" },
  hired:        { bg: "#dcfce7", text: "#14532d", border: "#4ade80" },
  rejected:     { bg: "#fee2e2", text: "#991b1b", border: "#fca5a5" },
  screened_out: { bg: "#fef3c7", text: "#92400e", border: "#fcd34d" },
};

const REC_COLORS: Record<string, string> = {
  "Strong Yes": "#16a34a", "Yes": "#2563eb", "Maybe": "#d97706", "No": "#dc2626",
};

const PAGE_SIZES = [10, 25, 50];
const STATUS_OPTIONS = ["new", "reviewed", "shortlisted", "hired", "rejected", "screened_out"];
const STATUS_LABELS: Record<string, string> = {
  new: "New", reviewed: "Reviewed", shortlisted: "Shortlisted", hired: "Hired", rejected: "Rejected", screened_out: "Screened Out",
};

export default function CandidatesPage() {
  const tz = useTenantTimezone();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [returnTo] = useState<string | undefined>(() => (location.state as any)?.returnTo);
  const isMobile = useIsMobile(768);
  const [search, setSearch]             = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage]                 = useState(1);
  const [pageSize, setPageSize]         = useState(25);
  const [sort, setSort]                 = useState<{ field: string; dir: "asc" | "desc" }>({ field: "applied_at", dir: "desc" });

  // Side panel
  const [panel, setPanel]           = useState<any | null>(null);
  const [panelDetail, setPanelDetail] = useState<any | null>(null);
  const [panelLoading, setPanelLoading] = useState(false);
  const [regenMsg, setRegenMsg]     = useState("");
  const [panelTab, setPanelTab]     = useState<"overview" | "actions" | "notes">("overview");
  const [notes, setNotes]           = useState<any[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [noteText, setNoteText]     = useState("");
  const [noteSaving, setNoteSaving] = useState(false);
  const [deletingNoteId, setDeletingNoteId] = useState<number | null>(null);

  // Bulk
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkStatus, setBulkStatus]   = useState("");

  // Modals
  const [editingCandidate, setEditingCandidate] = useState<any | null>(null);
  const [editForm, setEditForm]       = useState({ name: "", email: "", phone: "", location: "", current_role: "", experience_years: "" });
  const [interviewAppId, setInterviewAppId] = useState<number | null>(null);
  const [intScheduleMode, setIntScheduleMode] = useState<"direct" | "propose">("direct");
  const [intForm, setIntForm]         = useState({ scheduled_at: "", interview_date: "", interview_time: "10:00", duration_minutes: "60", interview_type: "video", location_or_link: "", notes: "", interviewer_id: "", additional_attendees: "" });

  // AI tools
  const [iqLoading, setIqLoading]     = useState(false);
  const [iqResult, setIqResult]       = useState<any | null>(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareResult, setCompareResult]   = useState<any | null>(null);
  const [compareAppMap, setCompareAppMap]   = useState<Record<string, number>>({});
  const [compareJobPicker, setCompareJobPicker] = useState<{ eligible: any[] } | null>(null);
  const [compareJobId, setCompareJobId]         = useState<number | "">("");
  const [shortlistingName, setShortlistingName] = useState<string | null>(null);
  const [outreachLoading, setOutreachLoading] = useState(false);
  const [outreachResult, setOutreachResult]   = useState<any | null>(null);
  const [outreachTone, setOutreachTone]       = useState("warm");
  const [outreachBody, setOutreachBody]       = useState("");
  const [outreachSubject, setOutreachSubject] = useState("");
  const [outreachSending, setOutreachSending] = useState(false);
  const [outreachHistory, setOutreachHistory] = useState<any[]>([]);
  const [showHistory, setShowHistory]         = useState(false);
  const [rejectConfirm, setRejectConfirm]     = useState<{ appId: number; email: string } | null>(null);

  // Screening invite from candidate panel
  const [screeningModal, setScreeningModal] = useState(false);
  const [screeningJobId, setScreeningJobId] = useState<number | "">("");

  // Propose Slots (reused in unified interview modal for "propose" mode)
  const [slotsAppId, setSlotsAppId] = useState<number | null>(null);
  const [slotsForm, setSlotsForm] = useState({
    interview_type: "video",
    duration_minutes: "60",
    location_or_link: "",
    interviewer_id: "",
    interviewer_name: "",
    interviewer_email: "",
    timezone: browserTimezone(),
  });
  const [slotsSending, setSlotsSending] = useState(false);

  const qc = useQueryClient();
  const confirm = useConfirm();
  const { toast } = useToast();
  const { downloading, download } = useBlockingDownload();

  // Close panel on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") closePanelAndReturn(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [returnTo]);

  // Open panel + load detail
  async function openPanel(c: any) {
    setPanel(c);
    setPanelDetail(null);
    setIqResult(null);
    setOutreachResult(null);
    setRegenMsg("");
    setPanelTab("overview");
    setNotes([]);
    setNoteText("");
    setPanelLoading(true);
    try {
      const detail = await getCandidate(c.candidate_id);
      setPanelDetail(detail);
    } catch { /* panel still works with list data */ }
    finally { setPanelLoading(false); }
  }

  async function loadNotes(candidateId: number) {
    setNotesLoading(true);
    try { setNotes(await getCandidateNotes(candidateId)); }
    catch { /* silent */ }
    finally { setNotesLoading(false); }
  }

  async function handleAddNote() {
    if (!panel || !noteText.trim()) return;
    setNoteSaving(true);
    try {
      const n = await addCandidateNote(panel.candidate_id, noteText.trim());
      setNotes(prev => [n, ...prev]);
      setNoteText("");
    } catch { toast("Failed to save note", "error"); }
    finally { setNoteSaving(false); }
  }

  async function handleDeleteNote(noteId: number) {
    if (!panel) return;
    setDeletingNoteId(noteId);
    try {
      await deleteCandidateNote(panel.candidate_id, noteId);
      setNotes(prev => prev.filter(n => n.id !== noteId));
    } catch { toast("Failed to delete note", "error"); }
    finally { setDeletingNoteId(null); }
  }

  function closePanel() { setPanel(null); setPanelDetail(null); setIqResult(null); setOutreachResult(null); setOutreachBody(""); setOutreachSubject(""); setShowHistory(false); setOutreachHistory([]); setNotes([]); setNoteText(""); }
  // Explicit user-initiated close (X, backdrop, Escape) — returns to the job pipeline if opened from one
  function closePanelAndReturn() { closePanel(); if (returnTo) navigate(returnTo); }

  // Queries — auto-open panel when ?highlight=candidateId is in the URL
  const { data: candidates = [], isLoading } = useQuery({
    queryKey: ["candidates"],
    queryFn: () => getCandidates(),
  });

  useEffect(() => {
    const highlightId = searchParams.get("highlight");
    if (!highlightId || !candidates.length) return;
    const match = (candidates as any[]).find((c: any) => String(c.candidate_id) === highlightId);
    if (match) {
      openPanel(match);
      // clean up URL after opening, but keep returnTo in history state so closing can navigate back
      setSearchParams({}, { replace: true, state: returnTo ? { returnTo } : undefined });
    }
  }, [candidates, searchParams]);

  const { data: employees = [] } = useQuery({ queryKey: ["employees"], queryFn: getEmployees });
  const { data: allJobs = [] }   = useQuery<any[]>({ queryKey: ["jobs"], queryFn: getJobs });
  const { data: screeningTemplates = [] } = useQuery({ queryKey: ["screening-templates"], queryFn: listScreeningTemplates });
  const { data: screeningDefaults } = useQuery({ queryKey: ["screening-defaults"], queryFn: getScreeningDefaults });

  const sendScreeningMut = useMutation({
    mutationFn: (data: any) => createScreeningSession(data),
    onSuccess: () => {
      toast("Screening invite sent!", "success");
      setScreeningModal(false);
      setScreeningJobId("");
    },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to send screening invite", "error"),
  });

  const { data: interviews = [] } = useQuery({
    queryKey: ["interviews", interviewAppId, panel?.application_id],
    queryFn: () => getInterviews(interviewAppId!, panel?.application_id ?? null),
    enabled: interviewAppId !== null,
  });

  // Filter + sort
  const filtered = [...candidates]
    .filter((c: any) => {
      if (statusFilter && c.status !== statusFilter) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return (c.name || "").toLowerCase().includes(q) ||
             (c.job_title || "").toLowerCase().includes(q) ||
             (c.email || "").toLowerCase().includes(q) ||
             (c.location || "").toLowerCase().includes(q);
    })
    .sort((a: any, b: any) => {
      const av = a[sort.field] ?? ""; const bv = b[sort.field] ?? "";
      const cmp = typeof av === "number" ? av - bv : String(av).localeCompare(String(bv));
      return sort.dir === "asc" ? cmp : -cmp;
    });

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage   = Math.min(page, totalPages);
  const paginated  = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);
  React.useEffect(() => { setPage(1); }, [search, statusFilter]);
  const toggleSort = (field: string) =>
    setSort(s => ({ field, dir: s.field === field && s.dir === "asc" ? "desc" : "asc" }));
  const sortIcon = (field: string) =>
    sort.field === field ? (sort.dir === "asc" ? " ↑" : " ↓") : " ↕";

  // Mutations
  const statusMutation = useMutation({
    mutationFn: ({ appId, status, notify = true }: { appId: number; status: string; notify?: boolean }) =>
      updateStatus(appId, status, notify),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["candidates"] });
      if (panel) setPanel((p: any) => p ? { ...p, status: vars.status } : p);
    },
  });

  const editMutation = useMutation({
    mutationFn: ({ candidateId, data }: { candidateId: number; data: object }) =>
      updateCandidateDetails(candidateId, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["candidates"] }); setEditingCandidate(null); },
  });

  const removeAppMutation = useMutation({
    mutationFn: (appId: number) => deleteApplication(appId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["candidates"] });
      setPanel((p: any) => p ? { ...p, application_id: null, has_formatted_cv: false, score: null, recommendation: null, ai_summary: null } : p);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (candidateId: number) => deleteCandidate(candidateId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["candidates"] }); closePanel(); },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to delete candidate — please try again", "error"),
  });

  const bulkMutation = useMutation({
    mutationFn: (data: { application_ids: number[]; status: string }) => bulkUpdateStatus(data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["candidates"] }); setSelectedIds(new Set()); setBulkStatus(""); },
  });

  const scheduleIntMutation = useMutation({
    mutationFn: ({ appId, data }: { appId: number; data: object }) => scheduleInterview(appId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["interviews", interviewAppId] });
      setIntForm({ scheduled_at: "", interview_date: "", interview_time: "10:00", duration_minutes: "60", interview_type: "video", location_or_link: "", notes: "", interviewer_id: "", additional_attendees: "" });
      toast("Interview scheduled!", "success");
      setTimeout(() => setInterviewAppId(null), 1200);
    },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to schedule interview — please try again", "error"),
  });

  const deleteIntMutation = useMutation({
    mutationFn: (id: number) => deleteInterview(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["interviews", interviewAppId] }),
  });

  // Helpers
  const handleDownloadCV = async (candidateId: number, applicationId?: number | null) => {
    await download(() => downloadFormattedCV(candidateId, applicationId), "CV not ready — try Regenerate CV first");
  };

  const handleDownloadDocx = async (candidateId: number, applicationId?: number | null) => {
    await download(() => downloadFormattedDocx(candidateId, applicationId), "Word CV not ready — try Regenerate CV first");
  };

  const handleDownloadRawCV = async (candidateId: number) => {
    await download(() => downloadRawCV(candidateId), "Failed to download original CV");
  };

  const openEdit = (c: any) => {
    setEditingCandidate(c);
    setEditForm({ name: c.name || "", email: c.email || "", phone: c.phone || "", location: c.location || "", current_role: c.current_role || "", experience_years: String(c.experience_years || "") });
  };

  // AI tools
  async function handleGenerateIQ() {
    if (!panel) return;
    setIqResult(null); setIqLoading(true); setOutreachResult(null); setOutreachBody(""); setOutreachSubject("");
    try { setIqResult(await generateInterviewQuestions(panel.candidate_id, panel.job_id ?? undefined)); }
    catch (e: any) { toast(e?.response?.data?.detail || "Failed to generate questions", "error"); }
    finally { setIqLoading(false); }
  }

  async function handleOutreach() {
    if (!panel) return;
    setOutreachResult(null); setOutreachBody(""); setOutreachSubject(""); setOutreachLoading(true); setIqResult(null);
    try {
      const r = await generateOutreachEmail(panel.candidate_id, panel.job_id ?? undefined, outreachTone);
      setOutreachResult(r);
      setOutreachSubject(r.subject || "");
      setOutreachBody(r.body || "");
    }
    catch (e: any) { toast(e?.response?.data?.detail || "Failed to generate email — please retry", "error"); }
    finally { setOutreachLoading(false); }
  }

  async function handleSendOutreach() {
    if (!panel || !outreachSubject || !outreachBody) return;
    setOutreachSending(true);
    try {
      await sendOutreachEmail(panel.candidate_id, {
        subject: outreachSubject,
        body: outreachBody,
        to_email: outreachResult?.email || panel.email,
        tone: outreachTone,
        job_id: panel.job_id,
      });
      toast(`Email sent to ${outreachResult?.email || panel.email}!`, "success");
      setOutreachResult(null); setOutreachBody(""); setOutreachSubject("");
      // refresh history
      const h = await getOutreachHistory(panel.candidate_id);
      setOutreachHistory(h);
      setShowHistory(true);
    }
    catch (e: any) { toast(e?.response?.data?.detail || "Failed to send email", "error"); }
    finally { setOutreachSending(false); }
  }

  async function loadOutreachHistory() {
    if (!panel) return;
    try {
      const h = await getOutreachHistory(panel.candidate_id);
      setOutreachHistory(h);
      setShowHistory(true);
    } catch { setShowHistory(true); }
  }

  async function handleCompare() {
    const eligible = (candidates as any[]).filter((c: any) => selectedIds.has(c.candidate_id) && c.application_id != null);
    if (eligible.length < 2) { toast("Select at least 2 candidates with applications", "error"); return; }
    if (eligible.length > 4) { toast("Select at most 4 candidates", "error"); return; }
    const jobIds = Array.from(new Set(eligible.map((c: any) => c.job_id).filter(Boolean)));
    // If candidates come from different jobs (or no job), ask the user which job to benchmark against
    if (jobIds.length !== 1) {
      setCompareJobId("");
      setCompareJobPicker({ eligible });
      return;
    }
    await runCompare(eligible, jobIds[0]);
  }

  async function runCompare(eligible: any[], jobId?: number) {
    const nameMap: Record<string, number> = {};
    eligible.forEach((c: any) => { nameMap[c.name] = c.application_id; });
    setCompareAppMap(nameMap); setCompareResult(null); setCompareLoading(true); setCompareJobPicker(null);
    try { setCompareResult(await compareCandidates(eligible.map((c: any) => c.application_id), jobId)); }
    catch (e: any) { toast(e?.response?.data?.detail || "Comparison failed", "error"); }
    finally { setCompareLoading(false); }
  }

  async function handleShortlistTop(candidateName: string) {
    const appId = compareAppMap[candidateName];
    if (!appId) { toast("Could not find application", "error"); return; }
    setShortlistingName(candidateName);
    try {
      await updateStatus(appId, "shortlisted");
      qc.invalidateQueries({ queryKey: ["candidates"] });
      toast(`${candidateName} moved to Shortlisted`, "success");
    } catch (e: any) { toast("Failed to shortlist", "error"); }
    finally { setShortlistingName(null); }
  }

  const statusCounts = STATUS_OPTIONS.reduce<Record<string, number>>((acc, s) => {
    acc[s] = (candidates as any[]).filter((c: any) => c.status === s).length;
    return acc;
  }, {});

  // AI summary from panelDetail
  const aiApp = panelDetail?.applications?.[0];
  const strengths: string[] = (() => {
    try { return JSON.parse(aiApp?.strengths || "[]"); } catch { return []; }
  })();
  const gaps: string[] = (() => {
    try { return JSON.parse(aiApp?.gaps || "[]"); } catch { return []; }
  })();

  // The API returns score_breakdown as an already-parsed object (json.loads in backend).
  // Safely handle both pre-parsed object and legacy JSON string.
  const parsedBreakdown: Record<string, any> | null = (() => {
    const bd = aiApp?.score_breakdown;
    if (!bd) return null;
    if (typeof bd === "object") return bd as Record<string, any>;
    try { return JSON.parse(bd as string); } catch { return null; }
  })();

  // Score breakdown — supports both old (number) and new ({score,max,reason}) format
  type BreakdownDim = { score: number; max: number; reason?: string };
  const scoreBreakdown: Record<string, BreakdownDim> | null = (() => {
    if (!parsedBreakdown) return null;
    const dims: Record<string, BreakdownDim> = {};
    const MAXES: Record<string, number> = { skills_match: 40, experience_match: 25, education_match: 15, overall_profile: 20 };
    const NON_DIM = new Set(["skills_found", "confidence", "confidence_reason", "profile_signals"]);
    for (const [k, v] of Object.entries(parsedBreakdown)) {
      if (NON_DIM.has(k)) continue;
      if (typeof v === "number") dims[k] = { score: v as number, max: MAXES[k] ?? 100 };
      else if (typeof v === "object" && v !== null && "score" in v) dims[k] = v as BreakdownDim;
    }
    return Object.keys(dims).length > 0 ? dims : null;
  })();

  // Scoring confidence
  const confidence: { level: string; reason?: string } | null = (() => {
    if (!parsedBreakdown?.confidence) return null;
    return { level: String(parsedBreakdown.confidence).toLowerCase(), reason: parsedBreakdown.confidence_reason || undefined };
  })();

  // Profile signals (AI-judged + preferred company matches)
  type ProfileSignals = {
    notable_employers?: string[];
    notable_institutions?: string[];
    fast_progression?: boolean;
    concrete_achievements?: boolean;
    open_source_or_publications?: boolean;
    preferred_company_matches?: string[];
  };
  const profileSignals: ProfileSignals | null =
    (parsedBreakdown?.profile_signals as ProfileSignals) || null;

  type RedFlags = {
    job_hopping?: boolean;
    avg_tenure_months?: number;
    longest_gap_months?: number;
    title_regression?: boolean;
    frequent_role_changes?: boolean;
    flags?: string[];
  };
  const redFlags: RedFlags | null = (parsedBreakdown?.red_flags as RedFlags) || null;
  const hasRedFlags = redFlags && (redFlags.job_hopping || redFlags.title_regression || redFlags.frequent_role_changes || (redFlags.longest_gap_months ?? 0) > 6);

  const domainMatch: { job_domain?: string; match_level?: string; bonus_applied?: number; note?: string } | null =
    parsedBreakdown?.domain_match || null;

  const learningSignals: { fast_skill_acquisition?: boolean; self_taught_evidence?: boolean; certifications_count?: number; career_pivots?: number; note?: string } | null =
    parsedBreakdown?.learning_signals || null;

  const hireDepiteGaps: boolean = parsedBreakdown?.hire_despite_gaps === true;
  const hireDespiteGapsReason: string | null = parsedBreakdown?.hire_despite_gaps_reason || null;

  const skillsMissingRequired: string[] = (() => {
    const v = parsedBreakdown?.skills_missing_required;
    if (Array.isArray(v)) return v;
    return [];
  })();
  const skillsMissingNice: string[] = (() => {
    const v = parsedBreakdown?.skills_missing_nice;
    if (Array.isArray(v)) return v;
    return [];
  })();

  const CAT_COLORS: Record<string, string> = {
    "Technical/Skills": "#1e3a8a", "Behavioral": "#7c3aed",
    "Role-Specific": "#0f766e", "Gap-Probing": "#b45309",
  };

  // Panel styles
  const panelW = 440;
  const panelStyle: React.CSSProperties = isMobile
    ? {
        position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 900,
        background: "white", borderRadius: "16px 16px 0 0",
        boxShadow: "0 -8px 40px rgba(0,0,0,0.2)",
        maxHeight: "88vh", overflowY: "auto",
        transition: "transform 0.3s ease",
      }
    : {
        position: "fixed", top: 0, right: 0, bottom: 0, width: panelW, zIndex: 900,
        background: "white", boxShadow: "-4px 0 32px rgba(0,0,0,0.12)",
        overflowY: "auto", transition: "transform 0.3s ease",
      };

  // Everything a row needs to render, derived once so the mobile cards and the
  // desktop table cannot drift apart on scoring thresholds or stage-age colours.
  function deriveRow(c: any) {
    const sc = STATUS_COLORS[c.status] || STATUS_COLORS.new;
    // Only colour-code scored candidates; unscored (null or 0 with no app) stay neutral
    const hasScore   = c.score != null && c.score > 0;
    const scoreColor = hasScore ? (c.score >= 70 ? "#16a34a" : c.score >= 50 ? "#d97706" : "#dc2626") : "#94a3b8";
    const scoreBg    = hasScore ? (c.score >= 70 ? "#dcfce7" : c.score >= 50 ? "#fef9c3" : "#fee2e2") : "#f1f5f9";
    const isSelected = selectedIds.has(c.candidate_id);
    const isOpen     = panel?.candidate_id === c.candidate_id;
    const noApp      = !c.application_id;   // candidate added but no application yet
    // Avatar colour from name
    const avatarColors = ["#1e3a8a","#0f766e","#7c3aed","#b45309","#1d4ed8","#dc2626","#0369a1"];
    const avatarBg = avatarColors[(c.name?.charCodeAt(0) || 0) % avatarColors.length];
    const initials = (c.name || "?").split(" ").slice(0,2).map((w: string) => w[0]?.toUpperCase()).join("");
    const expYears = c.experience_years && Number(c.experience_years) > 0 ? c.experience_years : null;
    // Time-in-stage: use status_changed_at if available, else applied_at
    const stageTs = c.status_changed_at || c.applied_at;
    const daysInStage = stageTs ? Math.floor((Date.now() - new Date(stageTs).getTime()) / 86400000) : null;
    const stageAgeColor = daysInStage == null ? null : daysInStage >= 7 ? "#dc2626" : daysInStage >= 3 ? "#d97706" : "#16a34a";
    const stageAgeBg   = daysInStage == null ? null : daysInStage >= 7 ? "#fee2e2" : daysInStage >= 3 ? "#fef9c3" : "#dcfce7";
    return { sc, hasScore, scoreColor, scoreBg, isSelected, isOpen, noApp, avatarBg, initials, expYears, daysInStage, stageAgeColor, stageAgeBg };
  }

  function toggleSelect(candidateId: number, checked: boolean) {
    const next = new Set(selectedIds);
    checked ? next.add(candidateId) : next.delete(candidateId);
    setSelectedIds(next);
  }

  // ── Shared dialog chrome ──────────────────────────────────────────────────
  // Centred dialog at >=768px, bottom-anchored full-width sheet with a drag
  // handle below. Applied as styles rather than a wrapper component so the six
  // existing dialogs keep their JSX trees intact.
  const sheetBackdrop = (zIndex: number, dim = 0.5): React.CSSProperties => ({
    position: "fixed", inset: 0, background: `rgba(0,0,0,${dim})`, zIndex,
    display: "flex", justifyContent: "center",
    alignItems: isMobile ? "flex-end" : "center",
    padding: isMobile ? 0 : 16,
  });
  const sheetPanel = (maxWidth: number, extra?: React.CSSProperties): React.CSSProperties => ({
    background: "white", width: "100%",
    maxWidth: isMobile ? undefined : maxWidth,
    borderRadius: isMobile ? "14px 14px 0 0" : 14,
    padding: isMobile ? "8px 18px 20px" : 28,
    boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
    maxHeight: isMobile ? "88vh" : "90vh",
    overflowY: "auto",
    ...extra,
  });
  const SheetHandle = () => isMobile
    ? <div style={{ width: 40, height: 4, background: "#e2e8f0", borderRadius: 2, margin: "2px auto 14px" }} />
    : null;

  // Escape dismisses whichever dialog is open, innermost first. The two forms
  // (Edit, Interview) are deliberately NOT backdrop-dismissable — a stray tap
  // beside a half-filled form should not discard it — so Escape is their only
  // keyboard exit besides Cancel.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (screeningModal)        { setScreeningModal(false); return; }
      if (rejectConfirm)         { setRejectConfirm(null); return; }
      if (compareResult)         { setCompareResult(null); return; }
      if (compareJobPicker)      { setCompareJobPicker(null); return; }
      if (interviewAppId != null){ setInterviewAppId(null); return; }
      if (editingCandidate)      { setEditingCandidate(null); return; }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [screeningModal, rejectConfirm, compareResult, compareJobPicker, interviewAppId, editingCandidate]);

  const emptyState = !isLoading && filtered.length === 0 && (
    <div style={{ textAlign: "center", padding: "60px 0", color: "#64748b" }}>
      <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" style={{ marginBottom: 12 }}><circle cx="9" cy="7" r="4"/><path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/><path d="M21 21v-2a4 4 0 0 0-3-3.85"/></svg>
      <div style={{ fontSize: 14 }}>No candidates found.</div>
    </div>
  );

  return (
    <div>
      <LoadingOverlay active={
        statusMutation.isPending || editMutation.isPending || removeAppMutation.isPending ||
        deleteMutation.isPending || bulkMutation.isPending || scheduleIntMutation.isPending ||
        deleteIntMutation.isPending || sendScreeningMut.isPending || downloading
      } />

      {/* ── Side Panel Backdrop ── */}
      {panel && (
        <div onClick={closePanelAndReturn} style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)",
          zIndex: 850, backdropFilter: "blur(2px)",
        }} />
      )}

      {/* ── Side Panel ── */}
      {panel && (
        <div style={panelStyle}>
          {/* Mobile drag handle */}
          {isMobile && <div style={{ width: 40, height: 4, background: "#e2e8f0", borderRadius: 2, margin: "12px auto 0" }} />}

          {/* Panel header */}
          <div style={{ padding: "20px 20px 0", position: "sticky", top: 0, background: "white", zIndex: 10, borderBottom: "1px solid #f1f5f9", paddingBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#0f172a", marginBottom: 4, textTransform: "capitalize" }}>{panel.name}</div>
                <div style={{ fontSize: 13, color: "#64748b" }}>
                  {panel.current_role && <span>{panel.current_role}</span>}
                  {panel.current_role && panel.experience_years && <span> · </span>}
                  {panel.experience_years && <span>{panel.experience_years} yrs exp</span>}
                </div>
                {panel.email && <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>{panel.email}</div>}
                {panel.phone && <div style={{ fontSize: 12, color: "#94a3b8" }}>📞 <a href={`tel:${panel.phone}`} style={{ color: "#0f766e", textDecoration: "none", fontWeight: 600 }}>{panel.phone}</a></div>}
                {panel.location && <div style={{ fontSize: 12, color: "#94a3b8" }}>📍 {panel.location}</div>}
              </div>
              <button onClick={closePanelAndReturn} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94a3b8", padding: 4, flexShrink: 0 }}>✕</button>
            </div>

            {/* Score + Rec + Status row */}
            <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
              {panel.score != null && (
                <span style={{
                  background: panel.score >= 70 ? "#dcfce7" : panel.score >= 50 ? "#fef9c3" : "#fee2e2",
                  color: panel.score >= 70 ? "#16a34a" : panel.score >= 50 ? "#d97706" : "#dc2626",
                  padding: "4px 12px", borderRadius: 20, fontSize: 13, fontWeight: 700,
                }}>{panel.score}/100</span>
              )}
              {panel.pool_rank != null && panel.pool_size != null && (
                <span style={{ background: "#f1f5f9", color: "#475569", padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                  #{panel.pool_rank} of {panel.pool_size}
                </span>
              )}
              {panel.score != null && screeningDefaults && panel.score >= screeningDefaults.auto_invite_threshold && (
                <span
                  title={`Match score ≥ ${screeningDefaults.auto_invite_threshold} (Recruiter Preferences > Screening). Suggestion only — no invite is sent automatically.`}
                  style={{ background: "#ede9fe", color: "#7c3aed", padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600 }}
                >🎥 Screening suggested</span>
              )}
              {panel.recommendation && (
                <span style={{
                  background: (REC_COLORS[panel.recommendation] || "#64748b") + "18",
                  color: REC_COLORS[panel.recommendation] || "#64748b",
                  padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                }}>{panel.recommendation}</span>
              )}
              {panel.status && (() => { const sc = STATUS_COLORS[panel.status]; return (
                <span style={{ background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`, padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600, textTransform: "capitalize" }}>
                  {panel.status}
                </span>
              ); })()}
            </div>

            {panel.job_title && (
              <div style={{ marginTop: 8, fontSize: 12, color: "#64748b" }}>
                Applied for: <strong style={{ color: "#334155" }}>{panel.job_title}</strong>
                {panel.applied_at && <span> · {formatDate(panel.applied_at, "short", tz)}</span>}
              </div>
            )}
            {panel.referred_by_name && (
              <div style={{ marginTop: 6, display: "inline-flex", alignItems: "center", gap: 5, background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 20, padding: "3px 10px", fontSize: 11, color: "#1d4ed8", fontWeight: 600 }}>
                👥 Referred by {panel.referred_by_name}
              </div>
            )}

            {/* Panel tabs */}
            <div style={{ display: "flex", gap: 0, marginTop: 14, borderBottom: "2px solid #f1f5f9" }}>
              {(["overview", "actions", "notes"] as const).map(t => {
                const labels: Record<string, string> = { overview: "Overview", actions: "Actions", notes: `Notes${notes.length > 0 ? ` (${notes.length})` : ""}` };
                const active = panelTab === t;
                return (
                  <button key={t} onClick={() => {
                    setPanelTab(t);
                    if (t === "notes" && notes.length === 0 && panel?.candidate_id) loadNotes(panel.candidate_id);
                  }}
                    style={{ padding: "8px 16px", fontSize: 13, fontWeight: active ? 700 : 500, color: active ? "#1e3a8a" : "#64748b", background: "none", border: "none", borderBottom: active ? "2px solid #1e3a8a" : "2px solid transparent", marginBottom: -2, cursor: "pointer", transition: "color 0.15s" }}>
                    {labels[t]}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 20 }}>

            {/* ── OVERVIEW TAB ── */}
            {panelTab === "overview" && <>

            {/* Knockout banner */}
            {panel.status === "screened_out" && aiApp?.knockout_reason && (
              <div style={{ background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 8, padding: "10px 14px", marginBottom: 10, display: "flex", gap: 8, alignItems: "flex-start" }}>
                <span style={{ fontSize: 15, flexShrink: 0 }}>🚫</span>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#92400e", marginBottom: 2 }}>Auto-screened out</div>
                  <div style={{ fontSize: 12, color: "#78350f", lineHeight: 1.5 }}>{aiApp.knockout_reason}</div>
                </div>
              </div>
            )}

            {/* Application History — shown for returning candidates */}
            {(() => {
              const allApps = panelDetail?.applications ?? [];
              const pastApps = allApps.filter((a: any) => a.application_id !== panel.application_id);
              if (pastApps.length === 0) return null;
              return (
                <section>
                  <SectionTitle>Application History</SectionTitle>
                  <div style={{ background: "#fdf4ff", border: "1px solid #e9d5ff", borderRadius: 8, padding: "10px 14px", marginBottom: 10 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#7c3aed", marginBottom: 8 }}>↩ Returning candidate — {pastApps.length} previous application{pastApps.length > 1 ? "s" : ""}</div>
                    {pastApps.map((a: any) => {
                      const statusColors: Record<string, { bg: string; text: string }> = {
                        hired: { bg: "#dcfce7", text: "#166534" },
                        offer: { bg: "#dbeafe", text: "#1e3a8a" },
                        interview: { bg: "#ede9fe", text: "#5b21b6" },
                        rejected: { bg: "#fee2e2", text: "#991b1b" },
                        screened_out: { bg: "#fef9c3", text: "#854d0e" },
                        new: { bg: "#f1f5f9", text: "#475569" },
                      };
                      const sc = statusColors[a.status] ?? statusColors.new;
                      const dateStr = formatDate(a.applied_at, "short", tz);
                      return (
                        <div key={a.application_id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid #f3e8ff" }}>
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: "#3b0764" }}>{a.job_title}</div>
                            <div style={{ fontSize: 11, color: "#9333ea", marginTop: 1 }}>{dateStr}{a.score != null ? ` · Score ${Math.round(a.score)}` : ""}</div>
                          </div>
                          <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 10, background: sc.bg, color: sc.text, textTransform: "capitalize", flexShrink: 0 }}>
                            {a.status?.replace("_", " ")}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </section>
              );
            })()}

            {/* AI Assessment */}
            <section>
              <SectionTitle>AI Assessment</SectionTitle>
              {panelLoading && <div style={{ color: "#94a3b8", fontSize: 13 }}>Loading…</div>}
              {!panelLoading && aiApp?.ai_summary && (
                <div style={{ fontSize: 13, color: "#334155", lineHeight: 1.6, background: "#f8fafc", borderRadius: 8, padding: "10px 14px", marginBottom: 10 }}>
                  {aiApp.ai_summary}
                </div>
              )}

              {/* Talent signals */}
              {!panelLoading && profileSignals && (() => {
                const chips: { icon: string; label: string; color: string; bg: string; border: string }[] = [];

                (profileSignals.preferred_company_matches || []).forEach(c =>
                  chips.push({ icon: "🎯", label: c, color: "#92400e", bg: "#fffbeb", border: "#fde68a" })
                );
                (profileSignals.notable_employers || []).forEach(e =>
                  chips.push({ icon: "🏢", label: e, color: "#1e3a8a", bg: "#eff6ff", border: "#bfdbfe" })
                );
                (profileSignals.notable_institutions || []).forEach(i =>
                  chips.push({ icon: "🎓", label: i, color: "#1e3a8a", bg: "#eff6ff", border: "#bfdbfe" })
                );
                if (profileSignals.fast_progression)
                  chips.push({ icon: "⚡", label: "Fast progression", color: "#7c3aed", bg: "#faf5ff", border: "#e9d5ff" });
                if (profileSignals.concrete_achievements)
                  chips.push({ icon: "✅", label: "Concrete achievements", color: "#065f46", bg: "#ecfdf5", border: "#a7f3d0" });
                if (profileSignals.open_source_or_publications)
                  chips.push({ icon: "📚", label: "Open source / publications", color: "#0f766e", bg: "#f0fdfa", border: "#99f6e4" });

                if (chips.length === 0) return null;
                return (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
                    {chips.map((chip, i) => (
                      <span key={i} style={{
                        display: "inline-flex", alignItems: "center", gap: 4,
                        background: chip.bg, border: `1px solid ${chip.border}`,
                        borderRadius: 20, padding: "4px 10px",
                        fontSize: 11, fontWeight: 600, color: chip.color,
                      }}>
                        {chip.icon} {chip.label}
                      </span>
                    ))}
                  </div>
                );
              })()}

              {/* Score breakdown */}
              {!panelLoading && scoreBreakdown && (() => {
                const DIM_LABELS: Record<string, string> = {
                  skills_match: "Skills Match",
                  experience_match: "Experience",
                  education_match: "Education",
                  overall_profile: "Overall Profile",
                };
                // Build overall_profile sub-signals from domain_match and red_flags
                const opSubSignals: { label: string; pts: number; color: string }[] = [];
                if (domainMatch?.bonus_applied && domainMatch.bonus_applied > 0)
                  opSubSignals.push({ label: `Domain match: ${domainMatch.note || domainMatch.match_level}`, pts: domainMatch.bonus_applied, color: "#16a34a" });
                if (redFlags?.job_hopping) opSubSignals.push({ label: "Job hopping", pts: -5, color: "#dc2626" });
                if ((redFlags?.longest_gap_months ?? 0) > 6) opSubSignals.push({ label: `Gap of ${redFlags!.longest_gap_months}mo`, pts: -3, color: "#dc2626" });
                if (redFlags?.title_regression) opSubSignals.push({ label: "Title regression", pts: -3, color: "#dc2626" });
                if (redFlags?.frequent_role_changes) opSubSignals.push({ label: "4+ employers in 5yr", pts: -3, color: "#dc2626" });
                if (profileSignals?.concrete_achievements) opSubSignals.push({ label: "Quantified achievements", pts: 0, color: "#0891b2" });
                if (profileSignals?.fast_progression) opSubSignals.push({ label: "Fast progression", pts: 0, color: "#0891b2" });

                return (
                  <div style={{ background: "#f8fafc", borderRadius: 10, padding: "12px 14px", marginBottom: 10, border: "1px solid #e2e8f0" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>Score Breakdown</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {Object.entries(scoreBreakdown).map(([key, dim]) => {
                        const pct = Math.round((dim.score / dim.max) * 100);
                        const barColor = pct >= 75 ? "#16a34a" : pct >= 50 ? "#d97706" : "#dc2626";
                        return (
                          <div key={key}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                              <span style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>{DIM_LABELS[key] || key}</span>
                              <span style={{ fontSize: 12, fontWeight: 700, color: barColor }}>{dim.score}<span style={{ color: "#94a3b8", fontWeight: 400 }}>/{dim.max}</span></span>
                            </div>
                            <div style={{ background: "#e2e8f0", borderRadius: 4, height: 6, overflow: "hidden" }}>
                              <div style={{ width: `${pct}%`, height: "100%", background: barColor, borderRadius: 4, transition: "width 0.4s ease" }} />
                            </div>
                            {dim.reason && (
                              <div style={{ fontSize: 11, color: "#64748b", marginTop: 4, lineHeight: 1.5 }}>{dim.reason}</div>
                            )}
                            {/* Overall Profile sub-signals */}
                            {key === "overall_profile" && opSubSignals.length > 0 && (
                              <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
                                {opSubSignals.map((s, i) => (
                                  <span key={i} style={{ fontSize: 10, padding: "2px 7px", borderRadius: 10, fontWeight: 600,
                                    background: s.color === "#dc2626" ? "#fef2f2" : s.color === "#16a34a" ? "#f0fdf4" : "#eff6ff",
                                    color: s.color, border: `1px solid ${s.color}22` }}>
                                    {s.pts > 0 ? `+${s.pts} ` : s.pts < 0 ? `${s.pts} ` : ""}{s.label}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* Red flags */}
              {!panelLoading && hasRedFlags && redFlags && (
                <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", marginBottom: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#b91c1c", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>⚑ Red Flags</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {(redFlags.flags && redFlags.flags.length > 0)
                      ? redFlags.flags.map((f: string, i: number) => (
                          <div key={i} style={{ fontSize: 12, color: "#991b1b", display: "flex", gap: 6, alignItems: "flex-start" }}>
                            <span style={{ flexShrink: 0, marginTop: 1 }}>•</span><span>{f}</span>
                          </div>
                        ))
                      : [
                          redFlags.job_hopping && `Average tenure ${redFlags.avg_tenure_months ?? "?"}mo — job hopping`,
                          redFlags.frequent_role_changes && "4+ employers in last 5 years",
                          (redFlags.longest_gap_months ?? 0) > 6 && `Employment gap of ${redFlags.longest_gap_months}mo`,
                          redFlags.title_regression && "Title regression detected",
                        ].filter(Boolean).map((f, i) => (
                          <div key={i} style={{ fontSize: 12, color: "#991b1b", display: "flex", gap: 6, alignItems: "flex-start" }}>
                            <span style={{ flexShrink: 0, marginTop: 1 }}>•</span><span>{f}</span>
                          </div>
                        ))
                    }
                  </div>
                </div>
              )}

              {/* Hire despite gaps — green nudge when scorer says yes */}
              {!panelLoading && hireDepiteGaps && hireDespiteGapsReason && (
                <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, padding: "10px 14px", marginBottom: 10, display: "flex", gap: 8, alignItems: "flex-start" }}>
                  <span style={{ fontSize: 15, flexShrink: 0 }}>💡</span>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#15803d", marginBottom: 2 }}>Consider despite skill gaps</div>
                    <div style={{ fontSize: 12, color: "#166534", lineHeight: 1.5 }}>{hireDespiteGapsReason}</div>
                  </div>
                </div>
              )}

              {/* Domain match */}
              {!panelLoading && domainMatch && domainMatch.match_level && domainMatch.match_level !== "none" && (
                <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, padding: "10px 14px", marginBottom: 10, display: "flex", gap: 8, alignItems: "flex-start" }}>
                  <span style={{ fontSize: 15, flexShrink: 0 }}>🏭</span>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#1e40af", marginBottom: 2 }}>
                      {domainMatch.match_level === "strong" ? "Strong domain fit" : "Partial domain fit"}
                      {domainMatch.bonus_applied ? <span style={{ fontWeight: 400, color: "#3b82f6" }}> · +{domainMatch.bonus_applied}pts</span> : null}
                    </div>
                    {domainMatch.note && <div style={{ fontSize: 12, color: "#1e3a8a", lineHeight: 1.5 }}>{domainMatch.note}</div>}
                  </div>
                </div>
              )}

              {/* Learning signals */}
              {!panelLoading && learningSignals && (learningSignals.fast_skill_acquisition || learningSignals.self_taught_evidence || (learningSignals.certifications_count ?? 0) >= 2) && (
                <div style={{ background: "#faf5ff", border: "1px solid #e9d5ff", borderRadius: 8, padding: "10px 14px", marginBottom: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#7c3aed", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>⚡ Learning Velocity</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: learningSignals.note ? 6 : 0 }}>
                    {learningSignals.fast_skill_acquisition && <span style={{ background: "#ede9fe", color: "#6d28d9", borderRadius: 20, padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>Fast skill pickup</span>}
                    {learningSignals.self_taught_evidence && <span style={{ background: "#ede9fe", color: "#6d28d9", borderRadius: 20, padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>Self-taught</span>}
                    {(learningSignals.certifications_count ?? 0) >= 1 && <span style={{ background: "#ede9fe", color: "#6d28d9", borderRadius: 20, padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>{learningSignals.certifications_count} cert{learningSignals.certifications_count !== 1 ? "s" : ""}</span>}
                    {(learningSignals.career_pivots ?? 0) > 0 && <span style={{ background: "#ede9fe", color: "#6d28d9", borderRadius: 20, padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>{learningSignals.career_pivots} pivot{learningSignals.career_pivots !== 1 ? "s" : ""}</span>}
                  </div>
                  {learningSignals.note && <div style={{ fontSize: 12, color: "#5b21b6", lineHeight: 1.5 }}>{learningSignals.note}</div>}
                </div>
              )}

              {/* Skills gap split — required vs nice-to-have */}
              {!panelLoading && (skillsMissingRequired.length > 0 || skillsMissingNice.length > 0) && (
                <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 14px", marginBottom: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Skill Gaps</div>
                  {skillsMissingRequired.length > 0 && (
                    <div style={{ marginBottom: skillsMissingNice.length > 0 ? 8 : 0 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "#dc2626", marginBottom: 4 }}>Must-have gaps</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                        {skillsMissingRequired.map((s: string, i: number) => (
                          <span key={i} style={{ background: "#fef2f2", color: "#b91c1c", border: "1px solid #fecaca", borderRadius: 12, padding: "2px 8px", fontSize: 11, fontWeight: 500 }}>{s}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {skillsMissingNice.length > 0 && (
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "#92400e", marginBottom: 4 }}>Nice-to-have gaps</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                        {skillsMissingNice.map((s: string, i: number) => (
                          <span key={i} style={{ background: "#fffbeb", color: "#78350f", border: "1px solid #fde68a", borderRadius: 12, padding: "2px 8px", fontSize: 11, fontWeight: 500 }}>{s}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Confidence flag — warn when the score is based on weak/garbled CV text */}
              {!panelLoading && confidence && confidence.level !== "high" && (() => {
                const isLow = confidence.level === "low";
                return (
                  <div style={{
                    display: "flex", gap: 8, alignItems: "flex-start",
                    background: isLow ? "#fef2f2" : "#fffbeb",
                    border: `1px solid ${isLow ? "#fecaca" : "#fde68a"}`,
                    borderRadius: 8, padding: "10px 12px", marginBottom: 10,
                  }}>
                    <span style={{ fontSize: 14, lineHeight: 1.4 }}>{isLow ? "⚠️" : "ℹ️"}</span>
                    <div style={{ fontSize: 12, color: isLow ? "#b91c1c" : "#92400e", lineHeight: 1.5 }}>
                      <strong>{isLow ? "Low confidence" : "Medium confidence"} — review manually.</strong>
                      {confidence.reason && <span> {confidence.reason}</span>}
                    </div>
                  </div>
                );
              })()}

              {!panelLoading && (strengths.length > 0 || gaps.length > 0) && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  {strengths.length > 0 && (
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#16a34a", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.04em" }}>Strengths</div>
                      {strengths.slice(0, 3).map((s, i) => (
                        <div key={i} style={{ fontSize: 12, color: "#166534", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 6, padding: "4px 8px", marginBottom: 4 }}>✓ {s}</div>
                      ))}
                    </div>
                  )}
                  {gaps.length > 0 && (
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#d97706", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.04em" }}>Gaps</div>
                      {gaps.slice(0, 3).map((g, i) => (
                        <div key={i} style={{ fontSize: 12, color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "4px 8px", marginBottom: 4 }}>⚠ {g}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {!panelLoading && !aiApp?.ai_summary && (
                <div style={{ fontSize: 13, color: "#94a3b8" }}>No AI assessment yet — CV needs to be processed.</div>
              )}
            </section>

            {/* Screening Answers — custom fields answered on the application form */}
            {!panelLoading && (aiApp?.screening_answers || []).length > 0 && (
              <section>
                <SectionTitle>Screening Answers</SectionTitle>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {aiApp.screening_answers.map((a: any) => (
                    <div key={a.question_id} style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 12, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 6, padding: "6px 10px" }}>
                      <span style={{ color: "#64748b" }}>
                        {a.label}{a.archived && <span style={{ color: "#cbd5e1" }}> (removed)</span>}
                      </span>
                      <span style={{ color: "#334155", fontWeight: 600, textAlign: "right" }}>
                        {Array.isArray(a.value) ? a.value.join(", ") : (a.value ?? "—")}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            </> /* end overview tab */}

            {/* ── ACTIONS TAB ── */}
            {panelTab === "actions" && <>

            {/* Status */}
            {panel.application_id && (
              <section>
                <SectionTitle>Move to</SectionTitle>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {STATUS_OPTIONS.filter(s => s !== panel.status).map(s => {
                    const sc = STATUS_COLORS[s];
                    const isThis = statusMutation.isPending && (statusMutation.variables as any)?.status === s;
                    return (
                      <button key={s}
                        onClick={() => {
                          if (s === "rejected") {
                            setRejectConfirm({ appId: panel.application_id, email: panel.email || "" });
                          } else {
                            statusMutation.mutate({ appId: panel.application_id, status: s });
                          }
                        }}
                        disabled={statusMutation.isPending}
                        style={{ background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`, padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: statusMutation.isPending ? "not-allowed" : "pointer", opacity: statusMutation.isPending && !isThis ? 0.45 : 1, transition: "opacity 0.15s" }}>
                        {isThis ? `${STATUS_LABELS[s]}…` : STATUS_LABELS[s]}
                      </button>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Files */}
            <section>
              <SectionTitle>Files</SectionTitle>
              {!panel.has_formatted_cv && (
                <div style={{ marginBottom: 10 }}>
                  <label style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "8px 16px", borderRadius: 8, border: "2px dashed #cbd5e1", background: "#f8fafc", color: "#475569", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                    📎 Upload CV (PDF or Word)
                    <input type="file" accept=".pdf,.doc,.docx" style={{ display: "none" }} onChange={async e => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      try {
                        await attachCV(panel.candidate_id, file);
                        toast("CV uploaded — processing in background (~30s)", "success");
                        setRegenMsg("✓ Uploading…");
                        setTimeout(() => setRegenMsg(""), 4000);
                      } catch (err: any) {
                        toast(err?.response?.data?.detail || "Upload failed", "error");
                      }
                      e.target.value = "";
                    }} />
                  </label>
                  <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 6 }}>No CV on file. Upload one to enable AI scoring.</div>
                </div>
              )}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {panel.has_formatted_cv && (
                  <>
                    <PanelBtn onClick={() => handleDownloadCV(panel.candidate_id, panel.application_id)} color="#0f766e">⬇ PDF CV</PanelBtn>
                    <PanelBtn onClick={() => handleDownloadDocx(panel.candidate_id, panel.application_id)} color="#1d4ed8">⬇ Word CV</PanelBtn>
                  </>
                )}
                {panel.has_raw_cv && (
                  <PanelBtn onClick={() => handleDownloadRawCV(panel.candidate_id)} color="#475569">⬇ Original CV</PanelBtn>
                )}
                <a
                  href={getCandidateProfilePdfUrl(panel.candidate_id)}
                  target="_blank"
                  rel="noreferrer"
                  style={{ display: "inline-flex", alignItems: "center", padding: "6px 14px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#f8fafc", color: "#7c3aed", fontSize: 13, fontWeight: 600, textDecoration: "none", cursor: "pointer" }}
                >
                  ⬇ Profile PDF
                </a>
                <PanelBtn onClick={async () => {
                  try {
                    await regenerateCV(panel.candidate_id, panel.application_id);
                    setRegenMsg("✓ Queued"); setTimeout(() => setRegenMsg(""), 3000);
                  } catch { setRegenMsg("✗ Failed"); }
                }} color="#475569">🔄 Regenerate CV{regenMsg ? ` ${regenMsg}` : ""}</PanelBtn>
              </div>
            </section>

            {/* Workflow */}
            {panel.application_id && (
              <section>
                <SectionTitle>Workflow</SectionTitle>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  <PanelBtn onClick={() => setInterviewAppId(panel.candidate_id ?? panel.id)} color="#7c3aed">📅 Schedule Interview</PanelBtn>
                  <PanelBtn onClick={() => { setSlotsAppId(panel.application_id); setInterviewAppId(panel.candidate_id ?? panel.id); setIntScheduleMode("propose"); }} color="#0369a1">🗓 Propose Slots</PanelBtn>
                  <PanelBtn onClick={() => { closePanel(); navigate(`/offers?candidate_id=${panel.candidate_id}`); }} color="#1e3a8a">📄 Offer Letter</PanelBtn>
                  {(screeningTemplates as any[]).length > 0 && (
                    <PanelBtn onClick={() => { setScreeningJobId(panel.job_id || ""); setScreeningModal(true); }} color="#0f766e">🎥 Send Screening</PanelBtn>
                  )}
                </div>
              </section>
            )}

            {/* AI Tools */}
            <section>
              <SectionTitle>AI Tools</SectionTitle>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: iqResult || outreachResult ? 12 : 0 }}>
                <PanelBtn onClick={handleGenerateIQ} color="#7c3aed" loading={iqLoading}>
                  {iqLoading ? "Generating…" : "🎯 Interview Questions"}
                </PanelBtn>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <select value={outreachTone} onChange={e => setOutreachTone(e.target.value)}
                    style={{ fontSize: 11, padding: "4px 8px", border: "1px solid #e2e8f0", borderRadius: 6, color: "#475569", background: "white", cursor: "pointer" }}>
                    <option value="warm">Warm</option>
                    <option value="formal">Formal</option>
                    <option value="urgent">Urgent</option>
                  </select>
                  <PanelBtn onClick={handleOutreach} color="#0f766e" loading={outreachLoading}>
                    {outreachLoading ? "Drafting…" : "✉ Outreach Email"}
                  </PanelBtn>
                </div>
              </div>

              {/* IQ results inline */}
              {iqResult && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 10, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span>Interview Questions — {iqResult.job_title || ""}</span>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <button onClick={() => {
                        const t = (iqResult.questions||[]).map((q:any) => `[${q.category}]\n${q.question}\n`).join("\n");
                        navigator.clipboard.writeText(t).then(() => toast("Copied!", "success"));
                      }} style={{ fontSize: 11, color: "#7c3aed", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>📋 Copy All</button>
                      <button onClick={() => setIqResult(null)} style={{ fontSize: 16, color: "#94a3b8", background: "none", border: "none", cursor: "pointer", lineHeight: 1, padding: "0 2px" }}>✕</button>
                    </div>
                  </div>
                  {["Technical/Skills", "Behavioral", "Role-Specific", "Gap-Probing"].map(cat => {
                    const qs = (iqResult.questions || []).filter((q: any) => q.category === cat);
                    if (!qs.length) return null;
                    const color = CAT_COLORS[cat] || "#475569";
                    return (
                      <div key={cat} style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 6, display: "flex", alignItems: "center", gap: 5 }}>
                          <span style={{ width: 6, height: 6, borderRadius: "50%", background: color, display: "inline-block" }} />{cat}
                        </div>
                        {qs.map((q: any, i: number) => (
                          <div key={i} style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 7, padding: "9px 12px", marginBottom: 6 }}>
                            <div style={{ fontSize: 12, color: "#0f172a", lineHeight: 1.5 }}>{q.question}</div>
                            {q.purpose && <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 4 }}>Purpose: {q.purpose}</div>}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Outreach email draft + history */}
              {outreachResult && (
                <div style={{ marginTop: 12, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.04em" }}>Outreach Draft</span>
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <button onClick={handleOutreach} disabled={outreachLoading}
                        style={{ fontSize: 11, padding: "3px 10px", background: "none", border: "1px solid #e2e8f0", borderRadius: 6, cursor: "pointer", color: "#64748b" }}>
                        {outreachLoading ? "…" : "↺ Regenerate"}
                      </button>
                      <button onClick={() => { setOutreachResult(null); setOutreachBody(""); setOutreachSubject(""); }} style={{ fontSize: 16, color: "#94a3b8", background: "none", border: "none", cursor: "pointer", lineHeight: 1, padding: "0 2px" }}>✕</button>
                    </div>
                  </div>
                  <input value={outreachSubject} onChange={e => setOutreachSubject(e.target.value)}
                    style={{ width: "100%", fontSize: 12, fontWeight: 600, padding: "6px 8px", border: "1px solid #e2e8f0", borderRadius: 6, marginBottom: 8, boxSizing: "border-box" as const, color: "#0f172a" }} />
                  <textarea value={outreachBody} onChange={e => setOutreachBody(e.target.value)}
                    rows={7} style={{ width: "100%", fontSize: 12, padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6, lineHeight: 1.6, resize: "vertical" as const, boxSizing: "border-box" as const, color: "#334155", fontFamily: "inherit", marginBottom: 10 }} />
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" as const }}>
                    <button onClick={() => handleSendOutreach()} disabled={outreachSending || !outreachSubject || !outreachBody}
                      style={{ fontSize: 12, padding: "6px 14px", background: "linear-gradient(135deg, #1e3a8a, #00c9a7)", color: "white", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>
                      {outreachSending ? "Sending…" : "✉ Send Email"}
                    </button>
                    <button onClick={() => navigator.clipboard.writeText(`Subject: ${outreachSubject}\n\n${outreachBody}`).then(() => toast("Copied!", "success"))}
                      style={{ fontSize: 12, padding: "6px 12px", background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 6, cursor: "pointer", fontWeight: 600, color: "#334155" }}>
                      📋 Copy
                    </button>
                    {outreachResult.email && (
                      <a href={`mailto:${outreachResult.email}?subject=${encodeURIComponent(outreachSubject)}&body=${encodeURIComponent(outreachBody)}`}
                        style={{ fontSize: 12, padding: "6px 12px", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 6, color: "#1d4ed8", textDecoration: "none", fontWeight: 600 }}>
                        Open in Mail App
                      </a>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 6 }}>
                    To: {outreachResult.email || "no email on file"} · Edit subject & body freely before sending
                  </div>
                </div>
              )}
              {/* Outreach history */}
              <div style={{ marginTop: 8 }}>
                <button onClick={() => showHistory ? setShowHistory(false) : loadOutreachHistory()}
                  style={{ fontSize: 11, background: "none", border: "none", color: "#64748b", cursor: "pointer", padding: 0, textDecoration: "underline" }}>
                  {showHistory ? "Hide" : "View"} outreach history
                </button>
                {showHistory && (
                  <div style={{ marginTop: 8 }}>
                    {outreachHistory.length === 0 ? (
                      <div style={{ fontSize: 12, color: "#94a3b8" }}>No outreach sent yet.</div>
                    ) : outreachHistory.map((h: any) => (
                      <div key={h.id} style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 12px", marginBottom: 8 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "#0f172a" }}>{h.subject}</div>
                        <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                          Sent by {h.sent_by} · {formatDateTime(h.sent_at, tz)}
                          {h.tone && <span style={{ marginLeft: 6, background: "#f1f5f9", borderRadius: 4, padding: "1px 6px" }}>{h.tone}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>

            {/* Edit & Remove */}
            <section>
              <SectionTitle>Manage</SectionTitle>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                <PanelBtn onClick={() => openEdit(panel)} color="#475569">✏ Edit Details</PanelBtn>
                {panel.application_id && (
                  <PanelBtn
                    onClick={async () => {
                      const ok = await confirm(`Remove ${panel.name}'s CV and application from this pipeline? The candidate record will remain.`, { confirmLabel: "Remove CV" });
                      if (ok) removeAppMutation.mutate(panel.application_id);
                    }}
                    color="#dc2626"
                    loading={removeAppMutation.isPending}
                  >
                    {removeAppMutation.isPending ? "Removing…" : "✕ Remove CV"}
                  </PanelBtn>
                )}
                <PanelBtn
                  onClick={async () => {
                    const ok = await confirm(`Permanently delete ${panel.name} from the system? This cannot be undone.`, { confirmLabel: "Delete Candidate" });
                    if (ok) deleteMutation.mutate(panel.candidate_id);
                  }}
                  color="#dc2626"
                  loading={deleteMutation.isPending}
                >
                  {deleteMutation.isPending ? "Deleting…" : "✕ Delete Candidate"}
                </PanelBtn>
              </div>
            </section>

            </> /* end actions tab */}

            {/* ── NOTES TAB ── */}
            {panelTab === "notes" && (
              <section>
                <SectionTitle>Notes</SectionTitle>
                {/* Add note input */}
                <div style={{ marginBottom: 16 }}>
                  <textarea
                    value={noteText}
                    onChange={e => setNoteText(e.target.value)}
                    placeholder="Add a note about this candidate…"
                    rows={3}
                    style={{ width: "100%", padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, resize: "vertical", boxSizing: "border-box", color: "#334155", outline: "none" }}
                  />
                  <button
                    onClick={handleAddNote}
                    disabled={noteSaving || !noteText.trim()}
                    style={{ marginTop: 6, padding: "6px 16px", background: noteText.trim() ? "#1e3a8a" : "#e2e8f0", color: noteText.trim() ? "white" : "#94a3b8", border: "none", borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: noteText.trim() ? "pointer" : "not-allowed" }}>
                    {noteSaving ? "Saving…" : "Add Note"}
                  </button>
                </div>
                {/* Notes list */}
                {notesLoading ? (
                  <div style={{ color: "#94a3b8", fontSize: 13 }}>Loading notes…</div>
                ) : notes.length === 0 ? (
                  <div style={{ color: "#94a3b8", fontSize: 13 }}>No notes yet. Add the first one above.</div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {notes.map((n: any) => (
                      <div key={n.id} style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 12px" }}>
                        <div style={{ fontSize: 13, color: "#0f172a", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{n.body}</div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
                          <div style={{ fontSize: 11, color: "#94a3b8" }}>
                            {n.author_name} · {formatDateTime(n.created_at, tz)}
                          </div>
                          <button onClick={() => handleDeleteNote(n.id)}
                            disabled={deletingNoteId === n.id}
                            style={{ background: "none", border: "none", color: "#ef4444", fontSize: 11, cursor: deletingNoteId === n.id ? "not-allowed" : "pointer", padding: "0 4px", fontWeight: 600, opacity: deletingNoteId === n.id ? 0.6 : 1 }}>
                            {deletingNoteId === n.id ? "Deleting…" : "Delete"}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}

          </div>
        </div>
      )}

      {/* ── Edit Modal ── */}
      {editingCandidate && (
        <div style={sheetBackdrop(1000, 0.4)}>
          <div style={sheetPanel(520)}>
            <SheetHandle />
            <h3 style={{ margin: "0 0 18px", color: "#1e3a8a", fontSize: 17 }}>Edit Candidate</h3>
            <div className="grid-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
              {[
                { label: "Name", field: "name", placeholder: "Full name" },
                { label: "Email", field: "email", placeholder: "email@example.com" },
                { label: "Phone", field: "phone", placeholder: "+1 234 567 8900" },
                { label: "Location", field: "location", placeholder: "City, Country" },
                { label: "Current Role", field: "current_role", placeholder: "Software Engineer" },
                { label: "Experience (years)", field: "experience_years", placeholder: "5" },
              ].map(({ label, field, placeholder }) => (
                <div key={field}>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 4 }}>{label}</label>
                  <input value={(editForm as any)[field]} onChange={e => setEditForm(f => ({ ...f, [field]: e.target.value }))}
                    placeholder={placeholder}
                    style={{ width: "100%", padding: "7px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13, boxSizing: "border-box" as const }} />
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => editMutation.mutate({ candidateId: editingCandidate.candidate_id, data: { ...editForm, experience_years: parseFloat(editForm.experience_years) || 0 } })}
                disabled={editMutation.isPending}
                style={{ background: "linear-gradient(135deg, #1e3a8a, #00c9a7)", color: "white", border: "none", padding: "9px 20px", borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                {editMutation.isPending ? "Saving..." : "Save"}
              </button>
              <button onClick={() => setEditingCandidate(null)} style={{ background: "none", border: "1px solid #e2e8f0", borderRadius: 7, padding: "9px 16px", cursor: "pointer", fontSize: 13, color: "#64748b" }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Interview Modal ── */}
      {interviewAppId !== null && (
        <div style={sheetBackdrop(1000, 0.4)}>
          <div style={sheetPanel(520)}>
            <SheetHandle />
            <h3 style={{ margin: "0 0 16px", color: "#1e3a8a", fontSize: 17 }}>Schedule Interview</h3>

            {(interviews as any[]).length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 8 }}>Scheduled Interviews</div>
                {(interviews as any[]).map((iv: any) => (
                  <div key={iv.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 12px", marginBottom: 6 }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{formatDateTime(iv.scheduled_at, tz)}</div>
                      <div style={{ fontSize: 12, color: "#64748b" }}>{iv.interview_type} · {iv.duration_minutes}min {iv.interviewer_name ? `· ${iv.interviewer_name}` : ""}</div>
                    </div>
                    <button onClick={async () => {
                      const ok = await confirm("Delete this interview? This cannot be undone.", { confirmLabel: "Delete", danger: true });
                      if (ok) deleteIntMutation.mutate(iv.id);
                    }} disabled={deleteIntMutation.isPending} style={{ color: "#dc2626", background: "none", border: "none", cursor: "pointer", fontSize: 12 }}>✕</button>
                  </div>
                ))}
              </div>
            )}

            {/* Mode toggle */}
            <div style={{ display: "flex", gap: 0, marginBottom: 8, border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
              {([["direct", "📅 Schedule Now"], ["propose", "🤖 Automatic Scheduling"]] as const).map(([mode, label]) => (
                <button key={mode} onClick={() => setIntScheduleMode(mode)}
                  style={{ flex: 1, padding: "9px 8px", fontSize: 12, fontWeight: intScheduleMode === mode ? 700 : 500, background: intScheduleMode === mode ? "#1e3a8a" : "white", color: intScheduleMode === mode ? "white" : "#64748b", border: "none", cursor: "pointer" }}>
                  {label}
                </button>
              ))}
            </div>
            <p style={{ margin: "0 0 16px", fontSize: 12, color: "#64748b" }}>
              {intScheduleMode === "direct"
                ? "Pick a date and time — ICS calendar invites go to the candidate, interviewer, and you immediately."
                : "The interviewer gets an email to pick 3 available slots. The candidate chooses one. A Meet link is auto-generated and ICS invites go to everyone."}
            </p>

            {intScheduleMode === "direct" ? (() => {
                const todayStr = new Date().toISOString().split("T")[0];
                const isToday = intForm.interview_date === todayStr;
                const nowMins = new Date().getHours() * 60 + new Date().getMinutes();
                return (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 4 }}>Date *</label>
                    <DateInput value={intForm.interview_date} min={todayStr}
                      onChange={e => setIntForm(f => ({ ...f, interview_date: e.target.value }))}
                     
                     
                      style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13, boxSizing: "border-box" as const, cursor: "pointer" }} />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 4 }}>Time *</label>
                    <select value={intForm.interview_time} onChange={e => setIntForm(f => ({ ...f, interview_time: e.target.value }))}
                      style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13, boxSizing: "border-box" as const, background: "white" }}>
                      {Array.from({ length: 96 }, (_, i) => {
                        const h = Math.floor(i / 4).toString().padStart(2, "0");
                        const m = ((i % 4) * 15).toString().padStart(2, "0");
                        const t = `${h}:${m}`;
                        const slotMins = parseInt(h) * 60 + parseInt(m);
                        if (isToday && slotMins <= nowMins) return null;
                        return <option key={t} value={t}>{t}</option>;
                      })}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 4 }}>Duration</label>
                    <select value={intForm.duration_minutes} onChange={e => setIntForm(f => ({ ...f, duration_minutes: e.target.value }))}
                      style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13, boxSizing: "border-box" as const }}>
                      {[15, 30, 45, 60, 90, 120].map(m => <option key={m} value={m}>{m} min</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 4 }}>Type</label>
                    <select value={intForm.interview_type} onChange={e => setIntForm(f => ({ ...f, interview_type: e.target.value }))}
                      style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13, boxSizing: "border-box" as const }}>
                      {["video", "in_person", "phone"].map(t => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 4 }}>Interviewer</label>
                    <select value={intForm.interviewer_id} onChange={e => setIntForm(f => ({ ...f, interviewer_id: e.target.value }))}
                      style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13, boxSizing: "border-box" as const }}>
                      <option value="">Select interviewer…</option>
                      {(employees as any[]).map((emp: any) => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 4 }}>Location / Meeting Link</label>
                  <input value={intForm.location_or_link} onChange={e => setIntForm(f => ({ ...f, location_or_link: e.target.value }))}
                    placeholder={intForm.interview_type === "in_person" ? "Office address / room…" : "Leave blank — Google Meet link will be auto-generated"}
                    style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13, boxSizing: "border-box" as const }} />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 4 }}>Additional Attendees <span style={{ fontWeight: 400, color: "#94a3b8" }}>(comma-separated emails)</span></label>
                  <input value={intForm.additional_attendees} onChange={e => setIntForm(f => ({ ...f, additional_attendees: e.target.value }))}
                    placeholder="e.g. cto@company.com, vp@company.com"
                    style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13, boxSizing: "border-box" as const }} />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 4 }}>Notes</label>
                  <textarea value={intForm.notes} onChange={e => setIntForm(f => ({ ...f, notes: e.target.value }))}
                    rows={2} style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13, boxSizing: "border-box" as const, resize: "vertical" }} />
                </div>
              </>
                );
              })() : (
              <>
                <div className="grid-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 4 }}>Interview Type</label>
                    <select value={slotsForm.interview_type} onChange={e => setSlotsForm(f => ({ ...f, interview_type: e.target.value }))}
                      style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13, boxSizing: "border-box" as const }}>
                      {["video", "in_person", "phone"].map(t => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 4 }}>Duration</label>
                    <select value={slotsForm.duration_minutes} onChange={e => setSlotsForm(f => ({ ...f, duration_minutes: e.target.value }))}
                      style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13, boxSizing: "border-box" as const }}>
                      {[15, 30, 45, 60, 90, 120].map(m => <option key={m} value={m}>{m} min</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 4 }}>Interview Timezone</label>
                  <TimezoneSelect value={slotsForm.timezone} onChange={tz => setSlotsForm(f => ({ ...f, timezone: tz }))} style={{ width: "100%", boxSizing: "border-box" }} />
                </div>
                {slotsForm.interview_type === "video" && (
                  <div style={{ marginBottom: 12, padding: "10px 14px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, fontSize: 12, color: "#15803d" }}>
                    🤖 The interviewer picks 3 slots, the candidate confirms one, and a Google Meet link is auto-generated.
                  </div>
                )}
                <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "14px 16px", marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.05em" }}>Interviewer</div>
                  <div style={{ marginBottom: 10 }}>
                    <select value={slotsForm.interviewer_id}
                      onChange={e => { const emp = (employees as any[]).find((x: any) => String(x.id) === e.target.value); setSlotsForm(f => ({ ...f, interviewer_id: e.target.value, interviewer_name: emp ? emp.name : f.interviewer_name, interviewer_email: emp ? (emp.email || f.interviewer_email) : f.interviewer_email })); }}
                      style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13, boxSizing: "border-box" as const, background: "white" }}>
                      <option value="">— or enter manually below —</option>
                      {(employees as any[]).map((emp: any) => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
                    </select>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <div>
                      <label style={{ display: "block", fontSize: 11, color: "#64748b", marginBottom: 3 }}>Name *</label>
                      <input value={slotsForm.interviewer_name} onChange={e => setSlotsForm(f => ({ ...f, interviewer_name: e.target.value }))} placeholder="John Smith"
                        style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13, boxSizing: "border-box" as const }} />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: 11, color: "#64748b", marginBottom: 3 }}>Email *</label>
                      <input type="email" value={slotsForm.interviewer_email} onChange={e => setSlotsForm(f => ({ ...f, interviewer_email: e.target.value }))} placeholder="john@company.com"
                        style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13, boxSizing: "border-box" as const }} />
                    </div>
                  </div>
                </div>
              </>
            )}

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => { setInterviewAppId(null); setIntScheduleMode("direct"); }} style={{ background: "none", border: "1px solid #e2e8f0", borderRadius: 7, padding: "9px 16px", cursor: "pointer", fontSize: 13, color: "#64748b" }}>Close</button>
              {intScheduleMode === "direct" ? (
                <button disabled={!intForm.interview_date || scheduleIntMutation.isPending}
                  onClick={() => {
                    const scheduledAt = `${intForm.interview_date}T${intForm.interview_time}`;
                    scheduleIntMutation.mutate({ appId: interviewAppId!, data: { ...intForm, application_id: panel?.application_id, scheduled_at: scheduledAt, timezone: browserTimezone(), duration_minutes: parseInt(intForm.duration_minutes), interviewer_id: intForm.interviewer_id ? parseInt(intForm.interviewer_id) : null } });
                  }}
                  style={{ background: !intForm.interview_date || scheduleIntMutation.isPending ? "#94a3b8" : "linear-gradient(135deg, #1e3a8a, #00c9a7)", color: "white", border: "none", padding: "9px 20px", borderRadius: 7, cursor: !intForm.interview_date || scheduleIntMutation.isPending ? "not-allowed" : "pointer", fontWeight: 600, fontSize: 13 }}>
                  {scheduleIntMutation.isPending ? "Scheduling…" : "Send Invites"}
                </button>
              ) : (
                <button
                  disabled={!slotsForm.interviewer_email || !slotsForm.interviewer_name || slotsSending}
                  onClick={async () => {
                    const appId = panel?.application_id || slotsAppId;
                    if (!appId) { toast("No application selected", "error"); return; }
                    setSlotsSending(true);
                    try {
                      await proposeInterviewSlots(appId, { interview_type: slotsForm.interview_type, duration_minutes: parseInt(slotsForm.duration_minutes), location_or_link: slotsForm.location_or_link, interviewer_id: slotsForm.interviewer_id ? parseInt(slotsForm.interviewer_id) : undefined, interviewer_name: slotsForm.interviewer_name, interviewer_email: slotsForm.interviewer_email, timezone: slotsForm.timezone });
                      toast("Availability request sent to interviewer!", "success");
                      setInterviewAppId(null);
                      setIntScheduleMode("direct");
                      setSlotsForm({ interview_type: "video", duration_minutes: "60", location_or_link: "", interviewer_id: "", interviewer_name: "", interviewer_email: "", timezone: browserTimezone() });
                    } catch (e: any) {
                      toast(e?.response?.data?.detail || "Failed to send request", "error");
                    } finally { setSlotsSending(false); }
                  }}
                  style={{ background: (!slotsForm.interviewer_email || !slotsForm.interviewer_name || slotsSending) ? "#94a3b8" : "linear-gradient(135deg, #0369a1, #0ea5e9)", color: "white", border: "none", padding: "9px 20px", borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                  {slotsSending ? "Sending…" : "Request Availability"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Compare Job Picker ── */}
      {compareJobPicker && (
        <div style={sheetBackdrop(1000, 0.5)} onClick={() => setCompareJobPicker(null)}>
          <div style={sheetPanel(420)} onClick={e => e.stopPropagation()}>
            <SheetHandle />
            <h3 style={{ margin: "0 0 8px", color: "#1e3a8a", fontSize: 17 }}>Which job to compare for?</h3>
            <p style={{ margin: "0 0 18px", fontSize: 13, color: "#64748b" }}>
              The selected candidates are from different jobs (or have no job). Pick a job to use as the benchmark for this comparison.
            </p>
            <select
              value={compareJobId}
              onChange={e => setCompareJobId(e.target.value ? Number(e.target.value) : "")}
              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 14, color: "#1e293b", marginBottom: 18 }}
            >
              <option value="">— select a job —</option>
              {(allJobs as any[]).map((j: any) => (
                <option key={j.id} value={j.id}>{j.title}{j.client ? ` — ${j.client}` : ""}</option>
              ))}
            </select>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setCompareJobPicker(null)} style={{
                flex: 1, padding: "10px 0", borderRadius: 8, border: "1.5px solid #e2e8f0",
                background: "white", fontSize: 14, cursor: "pointer", color: "#475569",
              }}>Cancel</button>
              <button
                disabled={!compareJobId}
                onClick={() => runCompare(compareJobPicker.eligible, compareJobId as number)}
                style={{
                  flex: 2, padding: "10px 0", borderRadius: 8, border: "none",
                  background: compareJobId ? "linear-gradient(135deg, #1e3a8a, #00c9a7)" : "#94a3b8",
                  color: "white", fontSize: 14, fontWeight: 700,
                  cursor: compareJobId ? "pointer" : "not-allowed",
                }}>Compare →</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Compare Modal ── */}
      {(compareLoading || compareResult) && (
        <div style={sheetBackdrop(1000, 0.5)} onClick={() => setCompareResult(null)}>
          <div style={sheetPanel(760)} onClick={e => e.stopPropagation()}>
            <SheetHandle />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
              <h3 style={{ margin: 0, color: "#1e3a8a", fontSize: 17 }}>AI Candidate Comparison</h3>
              <button onClick={() => setCompareResult(null)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94a3b8" }}>✕</button>
            </div>
            {compareLoading && <div style={{ textAlign: "center", padding: "40px 0", color: "#64748b" }}><div style={{ fontSize: 28, marginBottom: 12 }}>🤖</div>Comparing candidates…</div>}
            {compareResult && (
              <>
                {compareResult.summary && (
                  <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, padding: "12px 16px", marginBottom: 18, fontSize: 13, color: "#1e40af", lineHeight: 1.6 }}>
                    {compareResult.summary}
                  </div>
                )}
                <div style={{ marginBottom: 18 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>Ranking</div>
                  {(compareResult.ranking || []).map((r: any, i: number) => (
                    <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "10px 14px", background: i === 0 ? "#f0fdf4" : "#f8fafc", border: `1px solid ${i === 0 ? "#86efac" : "#e2e8f0"}`, borderRadius: 8, marginBottom: 8 }}>
                      <div style={{ fontSize: 20, fontWeight: 800, color: i === 0 ? "#16a34a" : "#94a3b8", minWidth: 28 }}>#{r.rank}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>{r.candidate_name}</div>
                        <div style={{ fontSize: 12, color: "#475569", marginTop: 3 }}>{r.verdict}</div>
                        <div style={{ display: "flex", gap: 16, marginTop: 6, fontSize: 11 }}>
                          <span style={{ color: "#16a34a" }}>✓ {r.top_strength}</span>
                          <span style={{ color: "#dc2626" }}>⚠ {r.main_concern}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {compareResult.comparison_table?.length > 0 && (
                  <div style={{ marginBottom: 18, overflowX: "auto" }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>Comparison</div>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: "#f8fafc" }}>
                          <th style={{ padding: "8px 12px", textAlign: "left", color: "#64748b", fontWeight: 600, fontSize: 12, border: "1px solid #e2e8f0" }}>Dimension</th>
                          {Object.keys(compareResult.comparison_table[0]?.candidates || {}).map((name: string) => (
                            <th key={name} style={{ padding: "8px 12px", textAlign: "left", color: "#1e3a8a", fontWeight: 600, fontSize: 12, border: "1px solid #e2e8f0" }}>{name}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {compareResult.comparison_table.map((row: any, i: number) => (
                          <tr key={i}>
                            <td style={{ padding: "8px 12px", fontWeight: 600, color: "#334155", border: "1px solid #e2e8f0", fontSize: 12 }}>{row.dimension}</td>
                            {Object.values(row.candidates || {}).map((val: any, j: number) => (
                              <td key={j} style={{ padding: "8px 12px", color: "#475569", border: "1px solid #e2e8f0", fontSize: 12 }}>{val}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {compareResult.recommendation && (
                  <div style={{ background: "#fef9c3", border: "1px solid #fde047", borderRadius: 8, padding: "12px 16px", fontSize: 13, color: "#854d0e", lineHeight: 1.6, marginBottom: 14 }}>
                    <strong>Recommendation:</strong> {compareResult.recommendation}
                  </div>
                )}
                {compareResult.ranking?.[0] && (() => {
                  const topName = compareResult.ranking[0].candidate_name;
                  return compareAppMap[topName] ? (
                    <button onClick={() => handleShortlistTop(topName)} disabled={shortlistingName === topName}
                      style={{ background: "linear-gradient(135deg,#16a34a,#15803d)", color: "white", border: "none", borderRadius: 7, padding: "9px 20px", cursor: "pointer", fontWeight: 600, fontSize: 13, opacity: shortlistingName === topName ? 0.6 : 1 }}>
                      {shortlistingName === topName ? "Moving…" : `✓ Shortlist ${topName}`}
                    </button>
                  ) : null;
                })()}
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Header ── */}
      <div className="page-header">
        <div>
          <h1 style={{ margin: 0, color: "#0f172a", fontSize: 24, fontWeight: 700 }}>All Candidates</h1>
          <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 14 }}>
            {filtered.length} {statusFilter || search ? "matching" : "total"} application{filtered.length !== 1 ? "s" : ""}
            {(statusFilter || search) && ` (${(candidates as any[]).length} total)`}
          </p>
        </div>
        <button onClick={() => exportCandidatesCsv(filtered)} disabled={isLoading || filtered.length === 0}
          title={isLoading ? "Candidates are still loading…" : undefined}
          style={{
          background: "white", border: "1px solid #e2e8f0", color: "#94a3b8",
          padding: "6px 12px", borderRadius: 6, cursor: isLoading || filtered.length === 0 ? "default" : "pointer",
          opacity: isLoading || filtered.length === 0 ? 0.5 : 1, fontSize: 12,
        }}>↓ CSV</button>
      </div>

      {/* ── Toolbar: tabs + search share one row, tabs scroll rather than wrap.
             Previously seven free-standing pills wrapped to three rows on a
             phone and search sat on a fourth, pushing the first result off
             screen. Matches the artifact's .toolbar and InterviewHubPage. ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 14, flexWrap: "wrap", marginBottom: 18 }}>
        <TabPillBar
          ariaLabel="Filter candidates by status"
          active={statusFilter}
          onSelect={setStatusFilter}
          style={{ marginBottom: 0 }}
          items={[
            { key: "", label: "All", count: (candidates as any[]).length },
            ...STATUS_OPTIONS.map(sKey => ({
              key: sKey,
              label: STATUS_LABELS[sKey],
              count: statusCounts[sKey] || 0,
            })),
          ]}
        />
        <input placeholder="Search name, job, email, location…" value={search} onChange={e => setSearch(e.target.value)}
          style={{ padding: isMobile ? "11px 14px" : "8px 14px", minHeight: isMobile ? 44 : undefined, borderRadius: 6, border: "1px solid #e2e8f0", fontSize: 13, width: isMobile ? "100%" : 280, outline: "none", color: "#1e293b", boxSizing: "border-box" }} />
      </div>

      {isLoading && <SkeletonTable rows={6} cols={5} />}

      {/* ── Bulk action bar ── */}
      {selectedIds.size > 0 && (() => {
        const eligibleAppIds = (candidates as any[]).filter((c: any) => selectedIds.has(c.candidate_id) && c.application_id != null).map((c: any) => c.application_id);
        const hasEligible = eligibleAppIds.length > 0;
        const noAppCount = selectedIds.size - eligibleAppIds.length;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 16px", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 10, marginBottom: 12, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#1d4ed8" }}>{selectedIds.size} selected</span>
            {noAppCount > 0 && (
              <span style={{ fontSize: 12, color: "#d97706", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 6, padding: "3px 8px" }}>
                {noAppCount} without application
              </span>
            )}
            <select value={bulkStatus} onChange={e => setBulkStatus(e.target.value)}
              style={{ padding: "6px 10px", border: "1px solid #bfdbfe", borderRadius: 6, fontSize: 13, background: "white" }}>
              <option value="">Move to status…</option>
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
            </select>
            <button onClick={() => {
              if (!bulkStatus) return;
              if (!hasEligible) { toast("None selected have an application", "error"); return; }
              bulkMutation.mutate({ application_ids: eligibleAppIds, status: bulkStatus });
            }}
              disabled={!bulkStatus || bulkMutation.isPending}
              style={{ background: hasEligible ? "#1d4ed8" : "#94a3b8", color: "white", border: "none", padding: "6px 16px", borderRadius: 6, cursor: bulkStatus ? "pointer" : "not-allowed", fontWeight: 600, fontSize: 13 }}>
              {bulkMutation.isPending ? "Updating…" : hasEligible ? `Apply to ${eligibleAppIds.length}` : "Apply"}
            </button>
            {eligibleAppIds.length >= 2 && eligibleAppIds.length <= 4 && (
              <button onClick={handleCompare} disabled={compareLoading}
                style={{ background: "linear-gradient(135deg,#7c3aed,#1e3a8a)", color: "white", border: "none", padding: "6px 16px", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                {compareLoading ? "Comparing…" : `🤖 AI Compare (${eligibleAppIds.length})`}
              </button>
            )}
            <button onClick={() => setSelectedIds(new Set())} style={{ background: "none", border: "none", color: "#64748b", cursor: "pointer", fontSize: 13 }}>Clear</button>
          </div>
        );
      })()}

      {/* ── List: cards below 768px, table above. Candidates is an approval-style
             list, and responsive.css pins .table-wrap tables to min-width 540px
             on mobile — so the table cannot fit a 375px screen no matter how many
             columns hide. ── */}
      {isMobile ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {paginated.map((c: any) => {
            const { sc, scoreColor, scoreBg, isSelected, isOpen, noApp,
                    avatarBg, initials, expYears, daysInStage, stageAgeColor, stageAgeBg } = deriveRow(c);
            const meta = [c.current_role, expYears ? `${expYears}y` : null, c.location].filter(Boolean).join(" · ");
            return (
              <div key={c.candidate_id} onClick={() => openPanel(c)}
                style={{
                  background: isOpen ? "#eff6ff" : isSelected ? "#f0f9ff" : "white",
                  border: `1px solid ${isOpen ? "#bfdbfe" : "#e2e8f0"}`, borderRadius: 10,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)", padding: 14,
                  opacity: noApp ? 0.75 : 1, cursor: "pointer",
                }}>
                <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <span onClick={e => e.stopPropagation()}
                    style={{ display: "grid", placeItems: "center", minWidth: 44, minHeight: 44, marginLeft: -10, marginTop: -10 }}>
                    <input type="checkbox" checked={isSelected}
                      aria-label={`Select ${c.name}`}
                      onChange={e => toggleSelect(c.candidate_id, e.target.checked)}
                      style={{ cursor: "pointer", width: 17, height: 17 }} />
                  </span>
                  <div style={{
                    width: 38, height: 38, borderRadius: "50%", flexShrink: 0,
                    background: avatarBg, color: "white", display: "grid", placeItems: "center",
                    fontSize: 13, fontWeight: 700, letterSpacing: 0.5,
                  }}>{initials}</div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: "6px 8px", alignItems: "flex-start" }}>
                      <div style={{ fontWeight: 600, fontSize: 15, color: "#0f172a", minWidth: 0, overflowWrap: "anywhere" }}>{c.name}</div>
                      {noApp
                        ? <span style={{ fontSize: 11, background: "#fef9c3", color: "#854d0e", border: "1px solid #fde047", borderRadius: 20, padding: "2px 8px", fontWeight: 600, whiteSpace: "nowrap" }}>No application</span>
                        : c.status && <span style={{ background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`, padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, textTransform: "capitalize", whiteSpace: "nowrap" }}>{c.status}</span>}
                    </div>
                    {meta && <div style={{ fontSize: 12, color: "#64748b", marginTop: 2, overflowWrap: "anywhere" }}>{meta}</div>}
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
                      {c.referred_by_name && (
                        <span style={{ fontSize: 11, background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", borderRadius: 20, padding: "1px 8px", fontWeight: 600 }}>Referred by {c.referred_by_name}</span>
                      )}
                      {(c.applied_jobs?.length ?? 0) > 1 && (
                        <span style={{ fontSize: 11, background: "#fdf4ff", color: "#7c3aed", border: "1px solid #e9d5ff", borderRadius: 20, padding: "1px 8px", fontWeight: 600 }}>Returning</span>
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: noApp ? "#64748b" : "#334155", fontStyle: noApp ? "italic" : "normal", overflowWrap: "anywhere" }}>
                      {c.job_title || "No application"}
                    </div>
                    <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                      {formatDate(c.applied_at, "compact", tz)}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
                    {daysInStage != null && stageAgeColor && (
                      <span title={`${daysInStage}d in this stage`} style={{ background: stageAgeBg!, color: stageAgeColor, border: `1px solid ${stageAgeColor}30`, padding: "2px 8px", borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{daysInStage}d</span>
                    )}
                    {noApp ? null : c.score != null ? (
                      <span style={{ background: scoreBg, color: scoreColor, border: `1px solid ${scoreColor}30`, padding: "2px 10px", borderRadius: 20, fontSize: 12, fontWeight: 700 }}>{c.score}</span>
                    ) : (
                      <span style={{ fontSize: 11, background: "#fef9c3", color: "#854d0e", border: "1px solid #fde047", borderRadius: 20, padding: "2px 8px", fontWeight: 600 }}>No CV yet</span>
                    )}
                  </div>
                </div>

                {c.has_formatted_cv && (
                  <div style={{ marginTop: 12 }} onClick={e => e.stopPropagation()}>
                    <AsyncButton onClick={() => handleDownloadCV(c.candidate_id, c.application_id)}
                      style={{ width: "100%", minHeight: 44, background: "white", border: "1px solid #cbd5e1", borderRadius: 6, cursor: "pointer", color: "#334155", fontSize: 13, fontWeight: 600 }}>
                      Download CV
                    </AsyncButton>
                  </div>
                )}
              </div>
            );
          })}
          {emptyState}
        </div>
      ) : (
        <div className="table-wrap" style={{ background: "white", borderRadius: 10, border: "1px solid #e2e8f0" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                <th style={{ padding: "10px 12px", width: 36 }}>
                  <input type="checkbox"
                    checked={paginated.length > 0 && paginated.every((c: any) => selectedIds.has(c.candidate_id))}
                    onChange={e => {
                      if (e.target.checked) setSelectedIds(new Set(paginated.map((c: any) => c.candidate_id)));
                      else setSelectedIds(new Set());
                    }} style={{ cursor: "pointer" }} />
                </th>
                {[
                  { label: "Candidate", field: "name" },
                  { label: "Job", field: "job_title", cls: "hide-mobile" },
                  { label: "Received", field: "applied_at", cls: "hide-mobile" },
                  { label: "Score & Rec", field: "score" },
                  { label: "Status", field: "status", cls: "hide-mobile" },
                  { label: "", field: "" },
                ].map(h => (
                  <th key={h.label} className={h.cls || ""}
                    onClick={h.field ? () => toggleSort(h.field) : undefined}
                    style={{ padding: "10px 16px", textAlign: "left", fontWeight: 600, color: "#475569", fontSize: 12, whiteSpace: "nowrap", cursor: h.field ? "pointer" : "default", userSelect: "none" }}>
                    {h.label}{h.field ? sortIcon(h.field) : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginated.map((c: any) => {
                const { sc, scoreColor, scoreBg, isSelected, isOpen, noApp,
                        avatarBg, initials, expYears, daysInStage, stageAgeColor, stageAgeBg } = deriveRow(c);

                return (
                  <tr key={c.candidate_id}
                    style={{ borderBottom: "1px solid #f1f5f9", background: isOpen ? "#eff6ff" : isSelected ? "#f0f9ff" : "white", cursor: "pointer", opacity: noApp ? 0.65 : 1 }}
                    onMouseEnter={e => { if (!isOpen && !isSelected) e.currentTarget.style.background = "#f8fafc"; }}
                    onMouseLeave={e => { if (!isOpen && !isSelected) e.currentTarget.style.background = noApp ? "white" : "white"; }}
                    onClick={() => openPanel(c)}
                  >
                    {/* Checkbox */}
                    <td style={{ padding: "12px 12px" }} onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={isSelected}
                        onChange={e => {
                          const next = new Set(selectedIds);
                          e.target.checked ? next.add(c.candidate_id) : next.delete(c.candidate_id);
                          setSelectedIds(next);
                        }} style={{ cursor: "pointer" }} />
                    </td>

                    {/* Candidate — avatar + name + meta */}
                    <td style={{ padding: "10px 16px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        {/* Avatar */}
                        <div style={{
                          width: 34, height: 34, borderRadius: "50%", flexShrink: 0,
                          background: avatarBg, color: "white",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 12, fontWeight: 700, letterSpacing: 0.5,
                        }}>{initials}</div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 13, color: isOpen ? "#1e3a8a" : "#0f172a", display: "flex", alignItems: "center", gap: 6 }}>
                            {c.name}
                            {c.referred_by_name && <span title={`Referred by ${c.referred_by_name}`} style={{ fontSize: 10, background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", borderRadius: 10, padding: "1px 6px", fontWeight: 600 }}>👥 {c.referred_by_name}</span>}
                            {(c.applied_jobs?.length ?? 0) > 1 && <span title={`Applied to ${c.applied_jobs.length} roles`} style={{ fontSize: 10, background: "#fdf4ff", color: "#7c3aed", border: "1px solid #e9d5ff", borderRadius: 10, padding: "1px 6px", fontWeight: 600 }}>↩ Returning</span>}
                          </div>
                          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1 }}>
                            {c.current_role && <span>{c.current_role}</span>}
                            {c.current_role && expYears && <span> · </span>}
                            {expYears && <span>{expYears}y</span>}
                            {(c.current_role || expYears) && c.location && <span> · </span>}
                            {c.location && <span>{c.location}</span>}
                          </div>
                          {/* Mobile: job + status under name */}
                          <div className="hide-desktop" style={{ marginTop: 4, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                            {c.job_title && <span style={{ fontSize: 11, color: "#64748b" }}>{c.job_title}</span>}
                            {noApp
                              ? <span style={{ fontSize: 10, background: "#fef9c3", color: "#854d0e", border: "1px solid #fde047", borderRadius: 10, padding: "1px 6px", fontWeight: 600 }}>No CV yet</span>
                              : c.status && <span style={{ background: sc.bg, color: sc.text, padding: "2px 7px", borderRadius: 10, fontSize: 10, fontWeight: 600, textTransform: "capitalize" }}>{c.status}</span>
                            }
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Job */}
                    <td className="hide-mobile" style={{ padding: "12px 16px", color: noApp ? "#94a3b8" : "#334155", fontSize: 13 }}>
                      {c.job_title || <span style={{ color: "#cbd5e1", fontStyle: "italic", fontSize: 12 }}>No application</span>}
                    </td>

                    {/* Received */}
                    <td className="hide-mobile" style={{ padding: "12px 16px", color: "#94a3b8", fontSize: 12, whiteSpace: "nowrap" }}>
                      {formatDate(c.applied_at, "compact", tz)}
                    </td>

                    {/* Score + Recommendation */}
                    <td style={{ padding: "12px 16px" }}>
                      {noApp ? (
                        <span style={{ fontSize: 11, background: "#fef9c3", color: "#854d0e", border: "1px solid #fde047", borderRadius: 10, padding: "2px 8px", fontWeight: 600 }}>No CV yet</span>
                      ) : c.score != null ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          <span style={{ background: scoreBg, color: scoreColor, border: `1px solid ${scoreColor}30`, padding: "2px 10px", borderRadius: 20, fontSize: 12, fontWeight: 700, display: "inline-block", width: "fit-content" }}>
                            {c.score}
                          </span>
                          {c.recommendation && (
                            <span style={{ fontSize: 10, color: REC_COLORS[c.recommendation] || "#94a3b8", fontWeight: 600 }}>
                              {c.recommendation}
                            </span>
                          )}
                        </div>
                      ) : <span style={{ color: "#cbd5e1", fontSize: 13 }}>—</span>}
                    </td>

                    {/* Status + time-in-stage */}
                    <td className="hide-mobile" style={{ padding: "12px 16px" }}>
                      {!noApp && c.status ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`, padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600, textTransform: "capitalize" }}>
                            {c.status}
                          </span>
                          {daysInStage != null && stageAgeColor && (
                            <span title={`${daysInStage}d in this stage`} style={{ background: stageAgeBg!, color: stageAgeColor, border: `1px solid ${stageAgeColor}30`, padding: "2px 7px", borderRadius: 10, fontSize: 10, fontWeight: 700 }}>
                              {daysInStage}d
                            </span>
                          )}
                        </div>
                      ) : <span style={{ color: "#e2e8f0" }}>—</span>}
                    </td>

                    {/* Right affordance */}
                    <td style={{ padding: "12px 12px", textAlign: "right", width: 40 }} onClick={e => e.stopPropagation()}>
                      {c.has_formatted_cv ? (
                        <AsyncButton onClick={() => handleDownloadCV(c.candidate_id, c.application_id)} title="Download PDF CV"
                          style={{ background: "none", border: "1px solid #e2e8f0", borderRadius: 6, padding: "4px 8px", cursor: "pointer", color: "#64748b", fontSize: 12 }}>⬇</AsyncButton>
                      ) : (
                        <span style={{ color: "#cbd5e1", fontSize: 16 }}>›</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {emptyState}
        </div>
      )}

      {/* ── Pagination ── */}
      {filtered.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 16, flexWrap: "wrap", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#64748b" }}>
            Rows per page:
            <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
              style={{ border: "1px solid #d1d5db", borderRadius: 5, padding: "3px 8px", fontSize: 13 }}>
              {PAGE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <span style={{ marginLeft: 8 }}>
              {(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, filtered.length)} of {filtered.length}
            </span>
          </div>
          <div style={{ display: "flex", gap: 4 }}>
            <PagBtn onClick={() => setPage(1)} disabled={safePage === 1}>«</PagBtn>
            <PagBtn onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage === 1}>‹</PagBtn>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - safePage) <= 2)
              .reduce<(number | "…")[]>((acc, p, idx, arr) => {
                if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push("…");
                acc.push(p);
                return acc;
              }, [])
              .map((p, i) =>
                p === "…"
                  ? <span key={`e-${i}`} style={{ padding: "4px 8px", fontSize: 13, color: "#94a3b8" }}>…</span>
                  : <PagBtn key={p} onClick={() => setPage(p as number)} active={safePage === p}>{p}</PagBtn>
              )}
            <PagBtn onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}>›</PagBtn>
            <PagBtn onClick={() => setPage(totalPages)} disabled={safePage === totalPages}>»</PagBtn>
          </div>
        </div>
      )}

      {/* Screening invite modal */}
      {screeningModal && panel && (
        <div style={sheetBackdrop(1100, 0.5)}
          onClick={() => setScreeningModal(false)}>
          <div style={sheetPanel(420)} onClick={e => e.stopPropagation()}>
            <SheetHandle />
            <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 6 }}>Send Screening Invite</div>
            <div style={{ fontSize: 13, color: "#64748b", marginBottom: 20 }}>
              Sending to <strong>{panel.name || panel.email}</strong>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 4 }}>Job</label>
              <select value={screeningJobId} onChange={e => setScreeningJobId(parseInt(e.target.value) || "")}
                style={{ width: "100%", border: "1px solid #e2e8f0", borderRadius: 6, padding: "8px 12px", fontSize: 14, boxSizing: "border-box" as const }}>
                <option value="">Select job with screening template…</option>
                {(screeningTemplates as any[]).map((t: any) => (
                  <option key={t.job_id} value={t.job_id}>{t.job_title}</option>
                ))}
              </select>
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setScreeningModal(false)}
                style={{ background: "#fff", color: "#0f766e", border: "1px solid #0f766e", borderRadius: 6, padding: "8px 16px", cursor: "pointer", fontWeight: 600, fontSize: 14 }}>
                Cancel
              </button>
              <button
                onClick={() => sendScreeningMut.mutate({ job_id: screeningJobId, candidate_email: panel.email, candidate_name: panel.name })}
                disabled={!screeningJobId || !panel.email || sendScreeningMut.isPending}
                style={{ background: "#0f766e", color: "#fff", border: "none", borderRadius: 6, padding: "9px 18px", cursor: "pointer", fontWeight: 600, fontSize: 14, opacity: !screeningJobId || !panel.email ? 0.5 : 1 }}>
                {sendScreeningMut.isPending ? "Sending…" : "Send Invite"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject confirmation modal */}
      {rejectConfirm && (
        <div style={sheetBackdrop(1000, 0.5)} onClick={() => setRejectConfirm(null)}>
          <div onClick={e => e.stopPropagation()} style={sheetPanel(420)}>
            <SheetHandle />
            <div style={{ fontSize: 28, marginBottom: 10 }}>✉️</div>
            <h3 style={{ margin: "0 0 8px", fontSize: 16, color: "#1e293b" }}>Notify candidate?</h3>
            <p style={{ margin: "0 0 6px", fontSize: 13, color: "#64748b" }}>
              Moving <strong>{panel?.name}</strong> to <strong>Rejected</strong>.
            </p>
            {rejectConfirm.email && (
              <p style={{ margin: "0 0 20px", fontSize: 13, color: "#64748b" }}>
                Send rejection email to <strong>{rejectConfirm.email}</strong>?
              </p>
            )}
            <div style={{ display: "flex", gap: 10, flexDirection: "column" }}>
              <button
                disabled={statusMutation.isPending}
                onClick={() => {
                  statusMutation.mutate({ appId: rejectConfirm.appId, status: "rejected", notify: true });
                  setRejectConfirm(null);
                }}
                style={{ padding: "10px 0", borderRadius: 8, border: "none", background: "#dc2626", color: "white", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>
                Update Status &amp; Send Email
              </button>
              <button
                disabled={statusMutation.isPending}
                onClick={() => {
                  statusMutation.mutate({ appId: rejectConfirm.appId, status: "rejected", notify: false });
                  setRejectConfirm(null);
                }}
                style={{ padding: "10px 0", borderRadius: 8, border: "1.5px solid #e2e8f0", background: "white", color: "#475569", fontWeight: 600, cursor: "pointer", fontSize: 14 }}>
                Update Status Only
              </button>
              <button
                onClick={() => setRejectConfirm(null)}
                style={{ padding: "8px 0", borderRadius: 8, border: "none", background: "none", color: "#94a3b8", cursor: "pointer", fontSize: 13 }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SectionTitle({ children }: { children?: React.ReactNode }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10, paddingBottom: 6, borderBottom: "1px solid #f1f5f9" }}>
      {children}
    </div>
  );
}

function PanelBtn({ children, onClick, color, loading: externalLoading }: { children?: React.ReactNode; onClick: () => any; color: string; loading?: boolean }) {
  const [internalLoading, setInternalLoading] = React.useState(false);
  const loading = externalLoading || internalLoading;
  const handleClick = async () => {
    if (loading) return;
    const result = onClick();
    if (result && typeof result.then === "function") {
      setInternalLoading(true);
      try { await result; } finally { setInternalLoading(false); }
    }
  };
  return (
    <button onClick={handleClick} disabled={loading}
      style={{
        background: color + "12", color, border: `1px solid ${color}30`,
        padding: "7px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600,
        cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.6 : 1,
        whiteSpace: "nowrap", transition: "opacity 0.15s",
      }}>
      {loading && !externalLoading ? `${children}…` : children}
    </button>
  );
}

function PagBtn({ onClick, disabled, active, children }: React.PropsWithChildren<{ onClick: () => void; disabled?: boolean; active?: boolean }>) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      minWidth: 32, height: 32, padding: "0 8px",
      border: "1px solid " + (active ? "#1e3a8a" : "#e2e8f0"),
      borderRadius: 6, fontSize: 13, cursor: disabled ? "default" : "pointer",
      background: active ? "#1e3a8a" : "white",
      color: active ? "white" : disabled ? "#cbd5e1" : "#334155",
      fontWeight: active ? 700 : 400,
    }}>{children}</button>
  );
}

function exportCandidatesCsv(candidates: any[]) {
  const headers = ["Name", "Job", "Email", "Location", "Experience (yrs)", "Score", "Status", "Applied At"];
  const rows = candidates.map(c => [
    c.name || "", c.job_title || "", c.email || "", c.location || "",
    c.experience_years ?? "", c.score ?? "", c.status || "",
    c.applied_at ? formatDate(c.applied_at) : "",
  ]);
  const csv = [headers, ...rows].map(r => r.map((v: any) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  // Leading BOM (bug-17) — without it, Excel opens this UTF-8 file as Windows-1252 and
  // renders any non-ASCII character (e.g. the em dash used for a blank field) as mojibake.
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = "candidates.csv"; a.click();
  URL.revokeObjectURL(url);
}
